import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, onSnapshot, updateDoc, writeBatch, increment, serverTimestamp } from 'firebase/firestore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
  useDroppable
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../../logo_colored_transp_no_text.png';
import {
  FiArrowLeft,
  FiUsers,
  FiGrid,
  FiCamera,
  FiBarChart2,
  FiInfo,
  FiSettings,
  FiSearch,
  FiDownload,
  FiTrash2,
  FiAlertTriangle,
  FiPrinter,
  FiX,
  FiLink,
  FiPlus,
  FiMessageSquare,
  FiCheck,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import { MdQrCode } from 'react-icons/md';

// Fordítási táblázat a kódokhoz
const translationMap = {
  // Korosztályok
  'mini': 'Mini',
  'gyerek': 'Gyerek',
  'junior': 'Junior',
  'kadet': 'Kadét',
  'fiatal_felnott': 'Fiatal felnőtt',
  'felnott': 'Felnőtt',
  'szenior': 'Szenior',
  'veteran': 'Veterán',

  // 3D kategóriák
  '3d_hdh_bb': 'BB (Barebow)',
  '3d_hdh_bg': 'BG (Fúvócső)',
  '3d_hdh_cb': 'CB (Csigás Barebow)',
  '3d_hdh_crb': 'CRB (Számszeríj)',
  '3d_hdh_cu': 'CU (Scope-os Csigás)',
  '3d_hdh_hu': 'HU (Vadász Csigás)',
  '3d_hdh_ol': 'OL (Olimpiai)',
  '3d_hdh_pbhb': 'PBHB (Pusztai íj (Tradi))',
  '3d_hdh_trlb': 'TRLB (Longbow)',
  '3d_hdh_trrb': 'TRRB (Vadászreflex)',
  '3d_ifaa_bbc': 'BB(C) (Barebow-Csigás)',
  '3d_ifaa_bbr': 'BB(R) (Barebow-Reflex)',
  '3d_ifaa_bhc': 'BH(C) (Vadászijász-Csigás)',
  '3d_ifaa_bhr': 'BH(R) (Vadászijász-Reflex)',
  '3d_ifaa_bl': 'BL (Korlátozott Vadászijász)',
  '3d_ifaa_bu': 'BU (Bowhunter Unlimited)',
  '3d_ifaa_fsc': 'FS(C) (Korl. nyílt o.-Csigás)',
  '3d_ifaa_fsr': 'FS(R) (Korl. nyílt o.-Reflex)',
  '3d_ifaa_fu': 'FU (Korlátlan nyílt osztály)',
  '3d_ifaa_hb': 'HB (Történelmi íj)',
  '3d_ifaa_hbm': 'HB(M) (Történelmi íj modern)',
  '3d_ifaa_lb': 'LB (Longbow)',
  '3d_ifaa_tr': 'TR (Tradicionális reflex)',

  // Terep kategóriák
  'terep_olimpiai': 'OL (Olimpiai)',
  'terep_csigas': 'CU (Scope-os Csigás)',
  'terep_barebow': 'BB (Barebow)',
  'terep_cb': 'CB (Csigás Barebow)',
  'terep_hu': 'HU (Vadász Csigás)',
  'terep_pbhb': 'PBHB (Pusztai íj (Tradi))',
  'terep_trlb': 'TRLB (Longbow)',
  'terep_trrb': 'TRRB (Vadászreflex)',

  // Pálya kategóriák
  'palya_wa_ol': 'R (Olimpiai reflexíj)',
  'palya_wa_cu': 'CU (Scope-os Csigás)',
  'palya_wa_hu': 'HU (Vadász Csigás)',
  'palya_wa_bb': 'B (Barebow)',
  'palya_wa_trrb': 'TRRB (Vadászreflex)',
  'palya_wa_trlb': 'TRLB (Longbow)',
  'palya_wa_pbhb': 'PBHB (Pusztai íj (Tradi))',
  'palya_wa_crb': 'CRB (Számszeríj)',
  'palya_ifaa_fu': 'FU (Freestyle Unlimited)',
  'palya_ifaa_fsr': 'FSR (FSR (Freestyle Ltd Recurve))',
  'palya_ifaa_fsc': 'FSC (FSC (Freestyle Ltd Compound))',
  'palya_ifaa_bbr': 'BBR (Barebow-Reflex)',
  'palya_ifaa_bbc': 'BBC (Barebow-Csigás)',
  'palya_ifaa_bu': 'BU (Bowhunter Unlimited)',
  'palya_ifaa_bl': 'BL (Bowhunter Limited)',
  'palya_ifaa_bhc': 'BHC (Vadászijász-Csigás)',
  'palya_ifaa_bhr': 'BHR (Vadászijász-Reflex)',
  'palya_ifaa_lb': 'LB (Longbow)',
  'palya_ifaa_hb': 'HB (Historical Bow)',
  'palya_ifaa_tr': 'TR (Traditional Bow)',

  // Történelmi kategóriák
  'tortenelmi_pusztai_modern': 'Modern pusztai íj',
  'tortenelmi_pusztai_termeszetes': 'Természetes pusztai íj',
  'tortenelmi_botij': 'Történelmi hosszúíj / Botíj',
  'tortenelmi_vadaszij': 'Hagyományos vadászíj',
  'tortenelmi_szamszerij': 'Történelmi számszeríj',
  'tortenelmi_bb': 'BB (Barebow)',

  // Örömijász kategóriák
  'orom_wa_ol': 'OL (Olimpiai reflexíj)',
  'orom_wa_cu': 'CU (Scope-os Csigás)',
  'orom_wa_hu': 'HU (Vadász Csigás)',
  'orom_wa_cb': 'CB (Csigás Barebow)',
  'orom_wa_bb': 'BB (Barebow)',
  'orom_wa_trrb': 'TRRB (Vadászreflex)',
  'orom_wa_trlb': 'TRLB (Longbow)',
  'orom_wa_pbhb': 'PBHB (Pusztai íj)',
  'orom_wa_crb': 'CRB (Számszeríj)',
  'orom_wa_bg': 'BG (Fúvócső)',

  // Egyéb
  'egyeb_wa_ol': 'OL (Olimpiai reflexíj)',
  'egyeb_wa_cu': 'CU (Scope-os Csigás)',
  'egyeb_wa_hu': 'HU (Vadász Csigás)',
  'egyeb_wa_cb': 'CB (Csigás Barebow)',
  'egyeb_wa_bb': 'BB (Barebow)',
  'egyeb_wa_trrb': 'TRRB (Vadászreflex)',
  'egyeb_wa_trlb': 'TRLB (Longbow)',
  'egyeb_wa_pbhb': 'PBHB (Pusztai íj)',
  'egyeb_wa_crb': 'CRB (Számszeríj)',
  'egyeb_wa_bg': 'BG (Fúvócső)',
  'egyeb_altalanos': 'Egyéb Általános'
};

const ageCategories = [
  { id: 'mini', label: 'Mini' },
  { id: 'gyerek', label: 'Gyerek' },
  { id: 'junior', label: 'Junior' },
  { id: 'kadet', label: 'Kadét' },
  { id: 'fiatal_felnott', label: 'Fiatal felnőtt' },
  { id: 'felnott', label: 'Felnőtt' },
  { id: 'szenior', label: 'Szenior' },
  { id: 'veteran', label: 'Veterán' }
];

const bowCategoriesByType = {
  '3d_hdh': [
    { id: '3d_hdh_bb', label: 'BB (Barebow)' },
    { id: '3d_hdh_bg', label: 'BG (Fúvócső)' },
    { id: '3d_hdh_cb', label: 'CB (Csigás Barebow)' },
    { id: '3d_hdh_crb', label: 'CRB (Számszeríj)' },
    { id: '3d_hdh_cu', label: 'CU (Scope-os Csigás)' },
    { id: '3d_hdh_hu', label: 'HU (Vadász Csigás)' },
    { id: '3d_hdh_ol', label: 'OL (Olimpiai)' },
    { id: '3d_hdh_pbhb', label: 'PBHB (Tradi)' },
    { id: '3d_hdh_trlb', label: 'TRLB (Longbow)' },
    { id: '3d_hdh_trrb', label: 'TRRB (Vadászreflex)' }
  ],
  '3d_ifaa': [
    { id: '3d_ifaa_bbc', label: 'BB(C) (Barebow-Csigás)' },
    { id: '3d_ifaa_bbr', label: 'BB(R) (Barebow-Reflex)' },
    { id: '3d_ifaa_bhc', label: 'BH(C) (Vadászijász-Csigás)' },
    { id: '3d_ifaa_bhr', label: 'BH(R) (Vadászijász-Reflex)' },
    { id: '3d_ifaa_bl', label: 'BL (Korlátozott Vadászijász)' },
    { id: '3d_ifaa_bu', label: 'BU (Bowhunter Unlimited)' },
    { id: '3d_ifaa_fsc', label: 'FS(C) (Korl. nyílt o.-Csigás)' },
    { id: '3d_ifaa_fsr', label: 'FS(R) (Korl. nyílt o.-Reflex)' },
    { id: '3d_ifaa_fu', label: 'FU (Korlátlan nyílt osztály)' },
    { id: '3d_ifaa_hb', label: 'HB (Történelmi íj)' },
    { id: '3d_ifaa_hbm', label: 'HB(M) (Történelmi íj modern)' },
    { id: '3d_ifaa_lb', label: 'LB (Longbow)' },
    { id: '3d_ifaa_tr', label: 'TR (Tradicionális reflex)' }
  ],
  'terep': [
    { id: 'terep_olimpiai', label: 'OL (Olimpiai)' },
    { id: 'terep_csigas', label: 'CU (Scope-os Csigás)' },
    { id: 'terep_barebow', label: 'BB (Barebow)' },
    { id: 'terep_cb', label: 'CB (Csigás Barebow)' },
    { id: 'terep_hu', label: 'HU (Vadász Csigás)' },
    { id: 'terep_pbhb', label: 'PBHB (Tradi - Pusztai íj)' },
    { id: 'terep_trlb', label: 'TRLB (Longbow)' },
    { id: 'terep_trrb', label: 'TRRB (Vadászreflex)' }
  ],
  'palya_wa': [
    { id: 'palya_wa_ol', label: 'R (Olimpiai reflexíj)' },
    { id: 'palya_wa_cu', label: 'CU (Scope-os Csigás)' },
    { id: 'palya_wa_hu', label: 'HU (Vadász Csigás)' },
    { id: 'palya_wa_bb', label: 'B (Barebow)' },
    { id: 'palya_wa_trrb', label: 'TRRB (Vadászreflex)' },
    { id: 'palya_wa_trlb', label: 'TRLB (Longbow)' },
    { id: 'palya_wa_pbhb', label: 'PBHB (Tradi - Pusztai íj)' },
    { id: 'palya_wa_crb', label: 'CRB (Számszeríj)' }
  ],
  'palya_ifaa': [
    { id: 'palya_ifaa_fu', label: 'FU (Freestyle Unlimited)' },
    { id: 'palya_ifaa_fsr', label: 'FSR (Freestyle Ltd Recurve)' },
    { id: 'palya_ifaa_fsc', label: 'FSC (Freestyle Ltd Compound)' },
    { id: 'palya_ifaa_bbr', label: 'BBR (Barebow Recurve)' },
    { id: 'palya_ifaa_bbc', label: 'BBC (Barebow Compound)' },
    { id: 'palya_ifaa_bu', label: 'BU (Bowhunter Unlimited)' },
    { id: 'palya_ifaa_bl', label: 'BL (Bowhunter Limited)' },
    { id: 'palya_ifaa_bhc', label: 'BHC (Bowhunter Compound)' },
    { id: 'palya_ifaa_bhr', label: 'BHR (Bowhunter Recurve)' },
    { id: 'palya_ifaa_lb', label: 'LB (Longbow)' },
    { id: 'palya_ifaa_hb', label: 'HB (Historical Bow)' },
    { id: 'palya_ifaa_tr', label: 'TR (Traditional Bow)' }
  ],
  'tortenelmi': [
    { id: 'tortenelmi_pusztai_modern', label: 'Modern pusztai íj' },
    { id: 'tortenelmi_pusztai_termeszetes', label: 'Természetes pusztai íj' },
    { id: 'tortenelmi_botij', label: 'Történelmi hosszúíj / Botíj' },
    { id: 'tortenelmi_vadaszij', label: 'Hagyományos vadászíj' },
    { id: 'tortenelmi_szamszerij', label: 'Történelmi számszeríj' },
    { id: 'tortenelmi_bb',label: 'BB (Barebow)' }
  ],
  'oromijasz': [
    { id: 'orom_wa_ol', label: 'OL (Olimpiai reflexíj)' },
    { id: 'orom_wa_cu', label: 'CU (Scope-os Csigás)' },
    { id: 'orom_wa_hu', label: 'HU (Vadász Csigás)' },
    { id: 'orom_wa_cb', label: 'CB (Csigás Barebow)' },
    { id: 'orom_wa_bb', label: 'BB (Barebow)' },
    { id: 'orom_wa_trrb', label: 'TRRB (Vadászreflex)' },
    { id: 'orom_wa_trlb', label: 'TRLB (Longbow)' },
    { id: 'orom_wa_pbhb', label: 'PBHB (Pusztai íj)' },
    { id: 'orom_wa_crb', label: 'CRB (Számszeríj)' },
    { id: 'orom_wa_bg',label: 'BG (Fúvócső)' }
  ],
  'egyeb': [
    { id: 'egyeb_wa_ol', label: 'OL (Olimpiai reflexíj)' },
    { id: 'egyeb_wa_cu', label: 'CU (Scope-os Csigás)' },
    { id: 'egyeb_wa_hu', label: 'HU (Vadász Csigás)' },
    { id: 'egyeb_wa_cb', label: 'CB (Csigás Barebow)' },
    { id: 'egyeb_wa_bb', label: 'BB (Barebow)' },
    { id: 'egyeb_wa_trrb', label: 'TRRB (Vadászreflex)' },
    { id: 'egyeb_wa_trlb', label: 'TRLB (Longbow)' },
    { id: 'egyeb_wa_pbhb', label: 'PBHB (Pusztai íj)' },
    { id: 'egyeb_wa_crb', label: 'CRB (Számszeríj)' },
    { id: 'egyeb_wa_bg',label: 'BG (Fúvócső)' },
    { id: 'egyeb_altalanos',label: 'Egyéb Általános' }
  ]
};

// --- SEGÉDFÜGGVÉNY: Fix szín generálása linkId alapján (Nagy kontrasztú paletta) ---
const getLinkColor = (linkId) => {
  if (!linkId) return 'transparent';
  const contrastColors = [
    '#E6194B', '#3CB44B', '#4363D8', '#F58231', '#911EB4',
    '#42D4F4', '#F032E6', '#228B22', '#B30000', '#008080',
    '#9A6324', '#800000', '#000075', '#333333', '#F0A000'
  ];
  let hash = 0;
  for (let i = 0; i < linkId.length; i++) {
    hash = linkId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return contrastColors[Math.abs(hash) % contrastColors.length];
};

// --- SORTABLE ITEM KOMPONENS ---
const SortableParticipant = ({ id, participant, isOverlay = false, isLinkBase = false, onLinkClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: id });

  // Megállapítjuk az effektív színt (vagy a meglévőt, vagy amit a host kapna)
  const effectiveLinkId = participant.linkId || (isLinkBase ? `group_${participant.id}` : null);
  const linkColor = getLinkColor(effectiveLinkId);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    padding: '10px',
    backgroundColor: isLinkBase ? 'transparent' : '#fff', // Host módban az animáció kezeli a színt
    border: participant.linkId ? `3px solid ${linkColor}` : '1px solid #eee',
    borderRadius: '6px',
    marginBottom: '8px',
    cursor: 'grab',
    boxShadow: isOverlay ? '0 5px 15px rgba(0,0,0,0.15)' : 'none',
    zIndex: isOverlay ? 1000 : 1,
    fontSize: '0.85rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'relative',
    '--pulse-color': linkColor,
    animation: isLinkBase ? 'brutal-pulse 0.6s infinite linear' : 'none'
  };

  const catShort = participant.bowCategory?.split('_').pop()?.toUpperCase() || '??';

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold' }}>{participant.fullName}</span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {onLinkClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLinkClick(participant.id);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: participant.linkId ? '#000' : '#b30000',
                cursor: 'pointer',
                display: 'flex',
                padding: '2px'
              }}
              title="Összekötés másokkal"
            >
              <FiLink size={16} />
            </button>
          )}
          <span style={catBadgeStyle}>{catShort}</span>
        </div>
      </div>
      {participant.comment && (
        <div style={teamCardCommentStyle} title={participant.comment}>
          "{participant.comment}"
        </div>
      )}
      <style>{`
        @keyframes brutal-pulse {
          0% { background-color: #fff; color: #333; transform: scale(1); box-shadow: 0 0 0px transparent; }
          50% { background-color: var(--pulse-color); color: #fff; transform: scale(1.05); box-shadow: 0 0 20px var(--pulse-color); }
          100% { background-color: #fff; color: #333; transform: scale(1); box-shadow: 0 0 0px transparent; }
        }
      `}</style>
    </div>
  );
};

// --- TEAM BOX KOMPONENS ---
const TeamBox = ({ teamNumber, participants, linkBaseId, onLinkClick, isArchived }) => {
  const { setNodeRef } = useDroppable({
    id: `team-${teamNumber}`,
  });

  return (
    <div ref={setNodeRef} style={teamBoxStyle}>
      <div style={teamHeaderStyle}>
        <span>{teamNumber}. csapat</span>
        <span style={teamCountStyle}>{participants.length} fő</span>
      </div>
      <SortableContext
        id={`team-${teamNumber}`}
        items={participants.map(p => p.id)}
        strategy={verticalListSortingStrategy}
      >
        <div style={teamBodyStyle}>
          {participants.map((p) => (
            <SortableParticipant
              key={p.id}
              id={p.id}
              participant={p}
              isLinkBase={linkBaseId === p.id}
              onLinkClick={isArchived ? null : onLinkClick} // ARCHIVÁLT VÉDELEM
            />
          ))}
          {participants.length === 0 && (
            <div style={emptyTeamTextStyle}>Húzz ide valakit...</div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// --- POOL BOX KOMPONENS ---
const PoolBox = ({ participants, onLinkClick, isArchived }) => {
  const { setNodeRef } = useDroppable({
    id: 'pool',
  });

  return (
    <div id="pool" ref={setNodeRef} style={{ flex: 1, backgroundColor: '#fdfdfd', borderRadius: '8px', border: '2px dashed #eee', padding: '10px', minHeight: '400px' }}>
      <SortableContext
        id="pool"
        items={participants.map(p => p.id)}
        strategy={verticalListSortingStrategy}
      >
        {participants.map((p) => (
          <SortableParticipant
            key={p.id}
            id={p.id}
            participant={p}
            onLinkClick={isArchived ? null : onLinkClick}
          />
        ))}
        {participants.length === 0 && (
          <div style={{ color: '#ccc', textAlign: 'center', marginTop: '30px', fontStyle: 'italic', fontSize: '0.8rem' }}>
            Mindenki csapatban van.
          </div>
        )}
      </SortableContext>
    </div>
  );
};

// --- QR-KÓD BEOLVASÓ KOMPONENS (JAVÍTOTT, DUPLA KAMERA MENTES) ---
const QrCodeScannerTab = ({ onScanSuccess, onSaveScores }) => {
  const [scannedResults, setScannedResults] = useState(null);
  const [rawQrText, setRawQrText] = useState('');

  useEffect(() => {
    let scannerInstance = null;
    let isMounted = true;

    import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
      // Ha közben lefutott a cleanup (pl. React Strict Mode miatt), ne hozzunk létre újat
      if (!isMounted) return;

      const container = document.getElementById("qr-reader-container");
      if (container) {
        container.innerHTML = ""; // Előző elem kiürítése biztonságból
      }

      scannerInstance = new Html5QrcodeScanner(
        "qr-reader-container",
        {
          fps: 20,
          qrbox: { width: 260, height: 260 },
          aspectRatio: 1.0,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        /* verbose= */ false
      );

      scannerInstance.render(
        (decodedText) => {
          setRawQrText(decodedText);

          if (onScanSuccess) {
            const resultsList = onScanSuccess(decodedText);
            if (resultsList) {
              setScannedResults(resultsList);
            }
          }
        },
        (errorMessage) => {
          // Folyamatos olvasási hibák elnyomása
        }
      );
    });

    return () => {
      isMounted = false;
      if (scannerInstance) {
        scannerInstance.clear().catch((error) => console.error("Kamera leállítási hiba:", error));
      }
      const container = document.getElementById("qr-reader-container");
      if (container) {
        container.innerHTML = "";
      }
    };
  }, []);

  // --- GOMBNYOMÁS KEZELÉSE: MENTÉS ÉS ELŐNÉZET ÜRÍTÉSE ---
  const handleConfirmAndSend = () => {
    if (onSaveScores && scannedResults) {
      onSaveScores(scannedResults);
      setScannedResults(null); // Kártyák törlése, készen áll a következő csapatra
      setRawQrText('');
    }
  };

  return (
    <div style={{ ...mainCardStyle, maxWidth: '1000px', margin: '0 auto' }}>
      <h3 style={{ ...resultGroupTitleStyle, marginBottom: '20px' }}>Helyszíni QR-kód beolvasás</h3>
      
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '25px',
        alignItems: 'flex-start',
        flexWrap: 'wrap'
      }}>
        
        {/* BAL OLDAL: Kamera Kép (Vízszintesen Tükrözve + CSS elrejtés a csúnya html5-qrcode gombokra) */}
        <div style={{ flex: '1 1 350px', minWidth: '300px' }}>
          <div 
            id="qr-reader-container" 
            style={{ 
              width: '100%', 
              borderRadius: '12px', 
              overflow: 'hidden',
              border: '2px dashed #ccc',
              backgroundColor: '#fafafa'
            }}
          ></div>

          <style>{`
            #qr-reader-container video {
              transform: scaleX(-1) !important;
              -webkit-transform: scaleX(-1) !important;
            }
            /* A html5-qrcode csúnya alapértelmezett gombjainak/szövegeinek eltüntetése/szépítése */
            #qr-reader-container__dashboard_section_csr button {
              background-color: #6c757d;
              color: white;
              border: none;
              padding: 6px 12px;
              border-radius: 6px;
              cursor: pointer;
              margin: 5px;
              font-weight: bold;
            }
            #qr-reader-container__dashboard_section_csr button:hover {
              background-color: #5a6268;
            }
          `}</style>
        </div>

        {/* JOBB OLDAL: Versenyzőnkénti részletes kártyák + MENTÉS GOMB */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          {scannedResults && scannedResults.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{ fontWeight: 'bold', color: '#28a745', fontSize: '1.1rem' }}>
                  🔍 Ellenőrizd a beolvasott csapatot ({scannedResults.length} fő)
                </span>
              </div>

              {/* VERSENYZŐK LISTÁJA KÜLÖN KÁRTYÁKON */}
              {scannedResults.map((comp, idx) => (
                <div key={idx} style={{
                  padding: '12px 15px',
                  backgroundColor: '#f8fdf9',
                  border: '1px solid #c3e6cb',
                  borderRadius: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  {/* Név, Kategória és Összpontszám */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#155724' }}>
                        {comp.fullName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#555' }}>
                        {comp.bowCategory} | {comp.ageCategory}
                      </div>
                    </div>
                    <div style={{ 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '8px', 
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      {comp.totalScore} pont
                    </div>
                  </div>

                  {/* TALÁLATI ZÓNÁK STATISZTIKÁJA */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '6px', 
                    flexWrap: 'wrap', 
                    marginTop: '4px', 
                    paddingTop: '6px', 
                    borderTop: '1px dashed #d4edda' 
                  }}>
                    {Object.entries(comp.hitZoneCounts)
                      .sort(([a], [b]) => Number(b) - Number(a))
                      .map(([zone, count]) => (
                        <span key={zone} style={{
                          backgroundColor: '#e2f0e5',
                          padding: '2px 7px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: '#155724',
                          fontWeight: 'bold'
                        }}>
                          {zone}p: <strong>{count}db</strong>
                        </span>
                      ))}
                  </div>
                </div>
              ))}

              {/* 🚀 KÜLDÉS AZ EREDMÉNYEKHEZ GOMB */}
              <button
                onClick={handleConfirmAndSend}
                style={{
                  marginTop: '10px',
                  padding: '14px 20px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(40, 167, 69, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'background-color 0.2s'
                }}
              >
                <span>🚀 Beküldés az eredményekhez</span>
              </button>

              {/* Nyers QR string összecsukható dobozban */}
              <details style={{ marginTop: '5px', fontSize: '0.75rem', color: '#666' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Nyers QR adatok megtekintése</summary>
                <code style={{
                  display: 'block',
                  backgroundColor: '#f1f1f1',
                  padding: '8px',
                  borderRadius: '6px',
                  marginTop: '5px',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace'
                }}>
                  {rawQrText}
                </code>
              </details>

            </div>
          ) : (
            <div style={{
              padding: '50px 20px',
              backgroundColor: '#fff',
              border: '2px dashed #e0e0e0',
              color: '#aaa',
              borderRadius: '10px',
              textAlign: 'center',
              fontStyle: 'italic',
              fontSize: '0.9rem'
            }}>
              Várakozás a csapat QR-kódjára...
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

const CompetitionOffice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeId, setActiveId] = useState(null);

  // --- ÚJ: CSAPAT ÁLLAPOT HOVER ---
  const [hoveredTeam, setHoveredTeam] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // --- ÚJ: REGISZTRÁCIÓS ÁLLAPOTOK (OFFLINE-BARÁT) ---
  const [localRegisteredIds, setLocalRegisteredIds] = useState(() => {
    // SZINKRON BETÖLTÉS: Azonnal beolvassuk a mentett állapotot indításkor
    const saved = localStorage.getItem(`reg_${id}`);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch (e) {
        console.error("Hiba a helyi adatok betöltésekor:", e);
      }
    }
    return new Set();
  });

  const [regSearchTerm, setRegSearchTerm] = useState('');

  // --- ÚJ: REGISZTRÁCIÓ LEZÁRÁSA ÁLLAPOT ---
  const [isRegistrationClosed, setIsRegistrationClosed] = useState(() => {
    return localStorage.getItem(`reg_closed_${id}`) === 'true';
  });

  // --- ÚJ: RÉSZLETES PONTOK ÁLLAPOTA ---
  const [allShotsByCompetitor, setAllShotsByCompetitor] = useState(() => {
    const saved = localStorage.getItem(`all_shots_${id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Hiba a részletes pontok betöltésekor:", e);
      }
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem(`all_shots_${id}`, JSON.stringify(allShotsByCompetitor));
  }, [allShotsByCompetitor, id]);

  // --- ÚJ: VÁRÓLISTA KERESŐ ---
  const [poolSearchTerm, setPoolSearchTerm] = useState('');

  // --- ÚJ: LÁNCBONTÁS MEGERŐSÍTŐ MODAL ÁLLAPOTOK ---
  const [showLinkBreakModal, setShowLinkBreakModal] = useState(false);
  const [pendingLinkData, setPendingLinkData] = useState(null);

  // Automatikus mentés LocalStorage-ba minden változáskor
  useEffect(() => {
    localStorage.setItem(`reg_${id}`, JSON.stringify(Array.from(localRegisteredIds)));
  }, [localRegisteredIds, id]);

  useEffect(() => {
    localStorage.setItem(`reg_closed_${id}`, isRegistrationClosed.toString());
  }, [isRegistrationClosed, id]);

  // --- ÚJ: KIJELÖLÉSI ÁLLAPOT ---
  const [selectedIds, setSelectedIds] = useState(new Set());

  // --- ÚJ: MEGJEGYZÉS POPUP ÁLLAPOT ---
  const [activeComment, setActiveComment] = useState(null);

  // --- ÚJ: ÖSSZEKÖTÉSI (LINK) ÁLLAPOTOK ---
  const [linkBaseId, setLinkBaseId] = useState(null);

  // Összekötés váltása
  const handleToggleLink = async (participantId) => {
    if (isArchived) return; // ARCHIVÁLT VÉDELEM

    // 1. Ha ő a bázis, akkor kikapcsoljuk a módot
    if (linkBaseId === participantId) {
      setLinkBaseId(null);
      return;
    }

    // 2. Ha nincs bázis, ő lesz az
    if (!linkBaseId) {
      setLinkBaseId(participantId);
      return;
    }

    // 3. Ha van már bázis, akkor őt hozzáadjuk a bázis csoportjához (vagy újat hozunk létre)
    const baseParticipant = participants.find(p => p.id === linkBaseId);
    const targetParticipant = participants.find(p => p.id === participantId);

    if (!baseParticipant || !targetParticipant) return;

    // Generálunk egy linkId-t, ha a bázisnak még nincs
    const newLinkId = baseParticipant.linkId || `group_${linkBaseId}`;

    try {
      const batch = writeBatch(db);

      // Frissítjük a bázist (ha kell)
      if (!baseParticipant.linkId) {
        batch.update(doc(db, "events", id, "participants", linkBaseId), { linkId: newLinkId });
      }

      // Frissítjük a célpontot
      // Ha már ugyanabban a csoportban van, akkor kivesszük belőle (lecsatoljuk)
      if (targetParticipant.linkId === newLinkId) {
        batch.update(doc(db, "events", id, "participants", participantId), { linkId: null });
      } else {
        batch.update(doc(db, "events", id, "participants", participantId), { linkId: newLinkId });
      }

      await batch.commit();
    } catch (e) {
      console.error("Hiba az összekötésnél:", e);
    }
  };

  // --- MODAL ÁLLAPOTOK A TÖRLÉSHEZ ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);

// --- ARCHERY NEXUS STÍLUSÚ MODAL ÁLLAPOT A MENTÉSHEZ ---
  const [scannedSaveSuccessData, setScannedSaveSuccessData] = useState(null);

  // --- ÚJ: MODAL ÁLLAPOTOK AZ ÚJ NEVEZŐHÖZ ---
  const [showAddModal, setShowAddModal] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    fullName: '',
    club: '',
    bowCategory: '',
    ageCategory: 'felnott',
    gender: 'Férfi',
    lunchCount: 0,
    comment: ''
  });

  // --- ÚJ: MODAL ÁLLAPOT A QR NYOMTATÁSHOZ ---
  const [showQrModal, setShowQrModal] = useState(false);

  // --- ÚJ: MODAL ÁLLAPOT A CSAPAT NYOMTATÁSHOZ ---
  const [showTeamsPrintModal, setShowTeamsPrintModal] = useState(false);

  // --- ÚJ: PÉNZÜGYI LÁTHATÓSÁG ---
  const [showFinancials, setShowFinancials] = useState(false);

  // --- ÚJ: MANUÁLIS EREDMÉNY RÖGZÍTÉS ÁLLAPOTOK ---
  const [showManualScoreModal, setShowManualScoreModal] = useState(false);
  const [manualSearchTerm, setManualSearchTerm] = useState('');
  const [manualScoreEntry, setManualScoreEntry] = useState({
    participant: null,
    zones: {}
  });
  const [manualScoreError, setManualScoreError] = useState(null);
  const [showCloseCompetitionError, setShowCloseCompetitionError] = useState(false);
  const [showFinalCloseConfirmModal, setShowFinalCloseConfirmModal] = useState(false);
  const [isArchived, setIsArchived] = useState(false); // ÚJ: Jelzi, ha az archívumból töltöttünk be

  // --- ÚJ: ID LÁTHATÓSÁG ---
  const [revealedIds, setRevealedIds] = useState(new Set());

  // --- ÚJ: NÉV LÁTHATÓSÁG ---
  const [revealedNames, setRevealedNames] = useState(new Set());

  // --- ÚJ: RENDEZÉSI ÁLLAPOT ---
  const [sortConfig, setSortConfig] = useState({ key: 'fullName', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const toggleIdVisibility = (id) => {
    const newSet = new Set(revealedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setRevealedIds(newSet);
  };

  // ÚJ: CÍM MEGVÁLTOZTATÁSA NYOMTATÁSKOR (Eltünteti a localhost feliratot a fejlécből)
  useEffect(() => {
    if (showTeamsPrintModal) {
      const originalTitle = document.title;
      document.title = " "; // Teljesen üres cím, így a Windows nem tudja odaírni az URL-t felülre
      return () => { document.title = originalTitle; };
    }
  }, [showTeamsPrintModal]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 1. Verseny alapadatok betöltése
  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        // Először megpróbáljuk az aktív versenyek közül
        let docRef = doc(db, "events", id);
        let docSnap = await getDoc(docRef);
        let archived = false;

        if (!docSnap.exists()) {
          // Ha ott nincs, megnézzük a lezártak között
          docRef = doc(db, "events_closed", id);
          docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            archived = true;
          }
        }

        if (docSnap.exists()) {
          const compData = { id: docSnap.id, ...docSnap.data() };
          setCompetition(compData);
          setIsArchived(archived);

          // SZINKRONIZÁLÁS: Ha archivált VAGY le van zárva a regisztráció, aktiváljuk a lakatot
          if (archived || compData.registrationClosed === true) {
            setIsRegistrationClosed(true);
          }
        } else {
          alert("A verseny nem található!");
          navigate('/egyesulet-dashboard');
        }
      } catch (error) {
        console.error("Hiba a verseny betöltésekor:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetition();
  }, [id, navigate]);

  // 2. Nevezők valós idejű betöltése ÉS ROBOT LOGIKA
  useEffect(() => {
    if (!id) return;

    // A gyűjtemény kiválasztása attól függően, hogy archivált-e
    const collectionName = isArchived ? "events_closed" : "events";
    const participantsRef = collection(db, collectionName, id, "participants");

    const unsubscribe = onSnapshot(participantsRef, async (snapshot) => {
      const participantsList = [];

      snapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        participantsList.push(data);
      });

      // --- ÚJ: Mentett pontok összefésülése a LocalStorage-ból ---
      const savedScores = JSON.parse(localStorage.getItem(`scores_${id}`) || '{}');
      participantsList.forEach(p => {
        if (savedScores[p.id] !== undefined) {
          p.totalScore = savedScores[p.id];
        }
      });

      participantsList.sort((a, b) => a.fullName?.localeCompare(b.fullName));
      setParticipants(participantsList);
    }, (error) => {
      console.error("Hiba a nevezők betöltésekor:", error);
    });

    return () => unsubscribe();
  }, [id, competition?.numberOfTargets, isArchived]); // HOZZÁADVA: isArchived és javított numberOfTargets

  // Drag & Drop eseménykezelő (Csoportos mozgatás támogatással)
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const activeParticipant = participants.find(p => p.id === activeId);
    if (!activeParticipant) return;

    // Megkeressük az összes tagot, akit mozgatunk (láncolt tagok)
    const movingMembers = activeParticipant.linkId
      ? participants.filter(p => p.linkId === activeParticipant.linkId)
      : [activeParticipant];

    let overTeamId = null;
    const overParticipant = participants.find(p => p.id === overId);
    if (overParticipant) {
      overTeamId = overParticipant.team;
    } else if (overId.toString().startsWith('team-')) {
      overTeamId = parseInt(overId.toString().split('-')[1]);
    }

    if (overTeamId && activeParticipant.team !== overTeamId) {
      const movingIds = new Set(movingMembers.map(m => m.id));
      setParticipants(prev => prev.map(p =>
        movingIds.has(p.id) ? { ...p, team: overTeamId } : p
      ));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const dragId = active.id;
    setActiveId(null);

    if (!over) return;

    const activeParticipant = participants.find(p => p.id === dragId);
    if (!activeParticipant) return;

    let finalTeamId = null;
    const overParticipant = participants.find(p => p.id === over.id);

    if (over.id === 'pool') {
      finalTeamId = null; // Visszahúzás a várólistára
    } else if (overParticipant) {
      finalTeamId = overParticipant.team;
    } else if (over.id.toString().startsWith('team-')) {
      finalTeamId = parseInt(over.id.toString().split('-')[1]);
    }

    if (finalTeamId !== undefined) {
      const movingMembers = activeParticipant.linkId
        ? participants.filter(p => p.linkId === activeParticipant.linkId)
        : [activeParticipant];

      // --- SPECIÁLIS ESET: Láncolt csoport visszahúzása a várólistára ---
      if (finalTeamId === null && activeParticipant.linkId) {
        setPendingLinkData({ members: movingMembers, teamId: null });
        setShowLinkBreakModal(true);
        return; // Megállunk, megvárjuk a döntést
      }

      try {
        const batch = writeBatch(db);
        movingMembers.forEach(member => {
          const pRef = doc(db, "events", id, "participants", member.id);
          const updateData = { team: finalTeamId };

          // HA A VÁRÓLISTÁRA KERÜL (team = null): Töröljük a láncolást is (linkId = null)
          if (finalTeamId === null) {
            updateData.linkId = null;
          }

          batch.update(pRef, updateData);
        });

        await batch.commit();
      } catch (e) {
        console.error("Hiba a mentéskor:", e);
      }
    }
  };

  // --- LÁNCBONTÁS MODAL KEZELŐK ---
  const handleConfirmKeepLink = async () => {
    if (!pendingLinkData) return;
    try {
      const batch = writeBatch(db);
      pendingLinkData.members.forEach(member => {
        const pRef = doc(db, "events", id, "participants", member.id);
        batch.update(pRef, { team: null }); // Team törlődik, linkId MARAD
      });
      await batch.commit();
    } catch (e) {
      console.error("Hiba a mentéskor:", e);
    } finally {
      setShowLinkBreakModal(false);
      setPendingLinkData(null);
    }
  };

  const handleConfirmBreakLink = async () => {
    if (!pendingLinkData) return;
    try {
      const batch = writeBatch(db);
      pendingLinkData.members.forEach(member => {
        const pRef = doc(db, "events", id, "participants", member.id);
        batch.update(pRef, { team: null, linkId: null }); // MINDEN TÖRÖLŐDIK
      });
      await batch.commit();
    } catch (e) {
      console.error("Hiba a mentéskor:", e);
    } finally {
      setShowLinkBreakModal(false);
      setPendingLinkData(null);
    }
  };

  // --- NEVEZŐ TÖRLÉSE FUNKCIÓ (MODAL NYITÁSA) ---
  const handleDeleteParticipant = (participant) => {
    if (isRegistrationClosed) return; // Ha lezárva, nem nyitható meg a törlés

    setParticipantToDelete(participant);
    setShowDeleteModal(true);
  };

  // --- MANUÁLIS NEVEZŐ MENTÉSE ---
  const handleSaveNewParticipant = async () => {
    if (!newParticipant.fullName || !newParticipant.bowCategory) {
      alert("A név és az íjkategória megadása kötelező!");
      return;
    }

    try {
      const batch = writeBatch(db);
      const guestCount = participants.filter(p => p.isGuest).length;
      const guestSuffix = `G${(guestCount + 1).toString().padStart(3, '0')}`;

      // Automata nevezési díj keresése a korosztály alapján
      const autoEntryFee = competition?.ageFees?.[newParticipant.ageCategory] || '';

      const finalClub = newParticipant.club.trim() === '' ? 'Egyéb' : newParticipant.club.trim();

      const newParticipantRef = doc(collection(db, "events", id, "participants"));
      const pData = {
        ...newParticipant,
        club: finalClub,
        userId: guestSuffix,
        isGuest: true,
        entryFee: autoEntryFee,
        status: 'Nevezve',
        createdAt: new Date().toISOString()
      };

      batch.set(newParticipantRef, pData);

      const eventRef = doc(db, "events", id);
      batch.update(eventRef, {
        participantsCount: increment(1)
      });

      await batch.commit();
      setShowAddModal(false);
      setNewParticipant({
        fullName: '', club: '', bowCategory: '', ageCategory: 'felnott',
        gender: 'Férfi', lunchCount: 0, comment: ''
      });
    } catch (error) {
      console.error("Hiba a mentés során:", error);
      alert("Hiba történt a mentés közben!");
    }
  };

  const handleFinalDeleteParticipant = async () => {
    if (!participantToDelete) return;

    try {
      const batch = writeBatch(db);
      const pRef = doc(db, "events", id, "participants", participantToDelete.id);
      batch.delete(pRef);

      const eventRef = doc(db, "events", id);
      batch.update(eventRef, {
        participantsCount: increment(-1)
      });

      await batch.commit();
      setShowDeleteModal(false);
      setParticipantToDelete(null);
    } catch (error) {
      console.error("Hiba a törlés során:", error);
      alert("Hiba történt a törlés közben!");
    }
  };

  const teamsData = useMemo(() => {
    const num = Number(competition?.numberOfTeams) || 20;
    const t = [];
    for (let i = 1; i <= num; i++) {
      t.push({
        number: i,
        members: participants.filter(p => Number(p.team) === i)
      });
    }
    return t;
  }, [participants, competition?.numberOfTeams]);

  // --- ÚJ: VÁRÓLISTA ADATOK ---
  const poolData = useMemo(() => {
    return participants
      .filter(p => !p.team)
      .filter(p => p.fullName?.toLowerCase().includes(poolSearchTerm.toLowerCase()))
      .sort((a, b) => a.fullName?.localeCompare(b.fullName));
  }, [participants, poolSearchTerm]);

  const resultsData = useMemo(() => {
    const filtered = participants.filter(p =>
      p.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.club?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const groups = {};
    filtered.forEach(p => {
      // HA LE VAN ZÁRVA A REGISZTRÁCIÓ: Csak a regisztráltakat tesszük az eredményekbe!
      if (isRegistrationClosed && !localRegisteredIds.has(p.id)) {
        return;
      }

      const key = `${p.bowCategory}_${p.ageCategory}_${p.gender}`;
      if (!groups[key]) {
        groups[key] = {
          bowCategory: p.bowCategory,
          ageCategory: p.ageCategory,
          gender: p.gender,
          members: []
        };
      }
      groups[key].members.push(p);
    });

    Object.values(groups).forEach(group => {
      group.members.sort((a, b) => {
        const scoreA = a.totalScore !== undefined ? Number(a.totalScore) : -1;
        const scoreB = b.totalScore !== undefined ? Number(b.totalScore) : -1;

        // Először a pontszám szerint csökkenőben (aki nincs beolvasva, az -1 pontot ér itt)
        if (scoreB !== scoreA) return scoreB - scoreA;
        return a.fullName?.localeCompare(b.fullName);
      });
    });

    return Object.values(groups).sort((a, b) => {
      const nameA = `${translationMap[a.bowCategory] || a.bowCategory} ${translationMap[a.ageCategory] || a.ageCategory}`;
      const nameB = `${translationMap[b.bowCategory] || b.bowCategory} ${translationMap[b.ageCategory] || b.ageCategory}`;
      return nameA.localeCompare(nameB);
    });
  }, [participants, searchTerm]);

  // --- ÚJ: ÉREM SZÁMLÁLÓ LOGIKA ---
  const medalStats = useMemo(() => {
    let gold = 0, silver = 0, bronze = 0;
    resultsData.forEach(group => {
      const count = group.members.length;
      if (count >= 1) gold++;
      if (count >= 2) silver++;
      if (count >= 3) bronze++;
    });
    return { gold, silver, bronze };
  }, [resultsData]);

  // --- ÚJ: BEREGISZTRÁLTAK (MEGÉRKEZETTEK) SZÁMÍTÁSA (HELYI MEMÓRIÁBÓL) ---
  const arrivedCount = useMemo(() => {
    return localRegisteredIds.size;
  }, [localRegisteredIds]);

  // --- ÚJ: DASHBOARD STATISZTIKÁK ---
  const dashboardStats = useMemo(() => {
    const stats = {
      totalLunches: 0,
      totalEntryFees: 0,
      totalLunchFees: 0,
      categories: {},
      arrivedLunches: 0
    };

    participants.forEach(p => {
      // Ebéd
      const count = Number(p.lunchCount) || 0;
      stats.totalLunches += count;
      if (localRegisteredIds.has(p.id)) {
        stats.arrivedLunches += count;
      }

      // Pénzügy
      const entryFee = Number(p.entryFee) || 0;
      const lunchPrice = Number(competition?.lunchPrice) || 0;
      stats.totalEntryFees += entryFee;
      stats.totalLunchFees += count * lunchPrice;

      // Kategóriák
      const catKey = p.bowCategory || 'Ismeretlen';
      if (!stats.categories[catKey]) {
        stats.categories[catKey] = { label: translationMap[catKey] || catKey, count: 0, arrived: 0 };
      }
      stats.categories[catKey].count++;
      if (localRegisteredIds.has(p.id)) {
        stats.categories[catKey].arrived++;
      }
    });

    return stats;
  }, [participants, localRegisteredIds, competition?.lunchPrice]);

  // --- ÚJ: REGISZTRÁCIÓ KEZELÉSE (ADATBÁZIS SZINKRONNAL) ---
  const handleToggleRegistrationLock = async () => {
    const newState = !isRegistrationClosed;
    setIsRegistrationClosed(newState);
    try {
      await updateDoc(doc(db, "events", id), { registrationClosed: newState });
    } catch (e) {
      console.error("Hiba a regisztráció állapot mentésekor:", e);
    }
  };

  const handleToggleRegistration = (participantId) => {
    if (isRegistrationClosed) return; // Ha lezárva, nem csinálunk semmit

    const newRegistered = new Set(localRegisteredIds);
    if (newRegistered.has(participantId)) {
      newRegistered.delete(participantId);
    } else {
      newRegistered.add(participantId);
    }
    setLocalRegisteredIds(newRegistered);
  };

  // --- ÚJ: MANUÁLIS FÁJLMENTÉS (CSV) ---
  const handleDownloadBackup = () => {
    const registeredList = participants
      .filter(p => localRegisteredIds.has(p.id))
      .map(p => `${p.fullName},${translationMap[p.bowCategory] || p.bowCategory},${translationMap[p.ageCategory] || p.ageCategory},${p.club || '-'}`)
      .join('\n');

    const header = "Név,Kategória,Korosztály,Egyesület\n";
    const blob = new Blob([header + registeredList], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `regisztraltak_${competition?.competitionName || 'lista'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

// --- CSAPAT QR-KÓD DEKÓDOLÁSA (ELŐNÉZET PREPARÁLÁS) ---
  const handleQrScanResult = (qrString) => {
    if (!qrString || qrString.length < 15) {
      alert("Érvénytelen vagy hiányos QR-kód!");
      return null;
    }

    try {
      // 1. Fejléc adatok kiolvasása
      const targetCount = parseInt(qrString.substring(3, 6), 10);
      const positiveScoresCount = parseInt(qrString.substring(6, 7), 16);
      
      const totalScoreValuesCount = positiveScoresCount + 1;
      const scoreValues = [];
      let cursor = 7;
      
      for (let i = 0; i < totalScoreValuesCount; i++) {
        const valStr = qrString.substring(cursor, cursor + 2);
        scoreValues.push(parseInt(valStr, 10));
        cursor += 2;
      }

      const startingTarget = parseInt(qrString.substring(cursor, cursor + 3), 10);
      cursor += 3;

      const scannedCompetitorsDetails = [];
      const labels = qrString.split(';')[0].split(','); // A találati értékek feliratai a fejlécből

      // 2. Versenyzők feldolgozása a 10 KARAKTERES azonosító alapján
      while (cursor < qrString.length) {
        const rawId = qrString.substring(cursor, cursor + 10).trim();
        const shotsPerTarget = parseInt(qrString.substring(cursor + 10, cursor + 12), 10);
        cursor += 12;

        const totalShotsForCompetitor = targetCount * shotsPerTarget;
        let competitorTotalScore = 0;
        const competitorShots = [];

        const hitZoneCounts = {};
        scoreValues.forEach(val => { hitZoneCounts[val] = 0; });

        for (let s = 0; s < totalShotsForCompetitor; s++) {
          if (cursor >= qrString.length) break;
          const hexIndex = parseInt(qrString.substring(cursor, cursor + 1), 16);
          cursor += 1;

          const scoreVal = scoreValues[hexIndex] !== undefined ? scoreValues[hexIndex] : 0;
          const label = labels[hexIndex] || '0';
          competitorTotalScore += scoreVal;

          const actualZoneLabel = label === 'X' ? 'X' : scoreVal.toString();
          if (hitZoneCounts[actualZoneLabel] !== undefined) {
            hitZoneCounts[actualZoneLabel] = (hitZoneCounts[actualZoneLabel] || 0) + 1;
          } else {
             hitZoneCounts[actualZoneLabel] = 1;
          }

          competitorShots.push({
            value: label,
            sum: scoreVal,
            target: Math.floor(s / shotsPerTarget) + 1, // Egyszerűsített sorszámozás
            position: (s % shotsPerTarget) + 1
          });
        }

        // PONTOS MATCH A TELJES AZONOSÍTÓ ALAPJÁN
        const participant = participants.find(p => {
          const fullId = generateParticipantId(p);
          return fullId === rawId || p.userId === rawId || p.id === rawId;
        });

        if (participant) {
          scannedCompetitorsDetails.push({
            id: participant.id,
            fullName: participant.fullName,
            bowCategory: translationMap[participant.bowCategory] || participant.bowCategory,
            ageCategory: translationMap[participant.ageCategory] || participant.ageCategory,
            gender: participant.gender || '-',
            totalScore: competitorTotalScore,
            hitZoneCounts: hitZoneCounts,
            shots: competitorShots
          });
        } else {
          scannedCompetitorsDetails.push({
            id: rawId,
            fullName: `Ismeretlen nevező (ID: ${rawId})`,
            bowCategory: '-',
            ageCategory: '-',
            gender: '-',
            totalScore: competitorTotalScore,
            hitZoneCounts: hitZoneCounts,
            shots: competitorShots
          });
        }
      }

      return scannedCompetitorsDetails;

    } catch (error) {
      console.error("Hiba a QR kód dekódolásakor:", error);
      alert("Nem sikerült feldolgozni a csapat QR-kódját!");
      return null;
    }
  };

  // --- MENTÉS AZ EREDMÉNYEKHEZ (ARCHERY NEXUS MODAL VISSZAJELZÉSSEL) ---
  const handleSaveScannedScores = (scannedList) => {
    if (!scannedList || scannedList.length === 0) return;

    // TILTÁS: Ha a verseny le van zárva, nem engedünk menteni
    if (competition?.status === 'Lezárt') {
      alert("A verseny le van zárva, az eredmények már nem módosíthatók.");
      return;
    }

    // 1. Betöltjük a korábbi pontokat LocalStorage-ból
    const savedScores = JSON.parse(localStorage.getItem(`scores_${id}`) || '{}');
    const newAllShots = { ...allShotsByCompetitor };

    // 2. Befrissítjük az új pontokat a LocalStorage objektumban és a Memóriában (state-ben) is
    scannedList.forEach(comp => {
      savedScores[comp.id] = comp.totalScore;
      if (comp.shots) {
        newAllShots[comp.id] = comp.shots;
      }

      // Frissítjük a memóriában lévő participants tömböt is
      setParticipants(prev => prev.map(p => 
        p.id === comp.id ? { ...p, totalScore: comp.totalScore } : p
      ));

      // Helyszíni regisztráció frissítése
      setLocalRegisteredIds(prev => new Set(prev).add(comp.id));
    });

    // 3. Mentés LocalStorage-ba PC-re
    localStorage.setItem(`scores_${id}`, JSON.stringify(savedScores));
    setAllShotsByCompetitor(newAllShots);

    // 4. Megnyitjuk a saját Archery Nexus stílusú modal ablakunkat!
    setScannedSaveSuccessData(scannedList);
  };

  // --- ÚJ: MANUÁLIS EREDMÉNY MENTÉSE ---
  const handleSaveManualScore = () => {
    if (!manualScoreEntry.participant) {
      setManualScoreError("Kérjük, válassz ki egy íjászt!");
      return;
    }

    // TILTÁS: Ha a verseny le van zárva, nem engedünk menteni
    if (competition?.status === 'Lezárt') {
      setManualScoreError("A verseny le van zárva, az eredmények már nem módosíthatók.");
      return;
    }

    const currentShots = Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0);

    // --- CRB KIVÉTEL A KERESÉSNÉL ---
    const bowCatShort = manualScoreEntry.participant.bowCategory?.split('_').pop()?.toUpperCase() || '';
    let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
    if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
      expectedShotsPerTarget = 1;
    }

    const maxShots = (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;

    if (currentShots !== maxShots) {
      setManualScoreError(`Hiba: A beírt lövések száma (${currentShots}) nem egyezik a versenyen leadható lövések számával (${maxShots})!`);
      return;
    }

    setManualScoreError(null);
    // Pontszám kiszámítása a zónák alapján
    let totalScore = 0;
    const manualShots = [];
    Object.entries(manualScoreEntry.zones).forEach(([zone, count]) => {
      const value = zone === 'X' ? 11 : parseInt(zone); // IFAA/3D-ben az X általában a legmagasabb
      const numCount = parseInt(count) || 0;
      totalScore += value * numCount;

      // Virtuális lövések létrehozása a zónák alapján
      for (let i = 0; i < numCount; i++) {
        manualShots.push({ value: zone, sum: value });
      }
    });

    // Mentés pontosan úgy, mint a QR-kódnál
    const savedScores = JSON.parse(localStorage.getItem(`scores_${id}`) || '{}');
    savedScores[manualScoreEntry.participant.id] = totalScore;

    localStorage.setItem(`scores_${id}`, JSON.stringify(savedScores));

    // Részletes pontok mentése
    setAllShotsByCompetitor(prev => ({
      ...prev,
      [manualScoreEntry.participant.id]: manualShots
    }));

    // Frissítés state-ben
    setParticipants(prev => prev.map(p =>
      p.id === manualScoreEntry.participant.id ? { ...p, totalScore: totalScore } : p
    ));
    setLocalRegisteredIds(prev => new Set(prev).add(manualScoreEntry.participant.id));

    // Modal bezárása és siker visszajelzés (újrahasznosítva a QR-kódos sikert)
    setShowManualScoreModal(false);
    setScannedSaveSuccessData([{
      fullName: manualScoreEntry.participant.fullName,
      totalScore: totalScore
    }]);
  };

  // --- ÚJ: VERSENY LEZÁRÁSA ---
  const handleCloseCompetition = async () => {
    const allScored = participants.length > 0 && participants.every(p => p.totalScore !== undefined);

    if (!allScored) {
      setShowCloseCompetitionError(true);
      setTimeout(() => setShowCloseCompetitionError(false), 5000); // 5 mp után eltűnik
      return;
    }

    setShowFinalCloseConfirmModal(true);
  };

  const handleFinalConfirmCloseCompetition = async () => {
    try {
      // 1. Státusz frissítése az eredeti helyen (Push értesítés kiváltása)
      const eventRef = doc(db, "events", id);
      await updateDoc(eventRef, {
        status: 'Lezárt',
        closedAt: serverTimestamp()
      });

      const batch = writeBatch(db);

      // 2. Csoportosítás és Helyezés kiszámítása
      const groups = {};
      participants.forEach(p => {
        const key = `${p.bowCategory}_${p.ageCategory}_${p.gender}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(p);
      });

      const finalParticipantsWithRank = [];

      Object.values(groups).forEach(group => {
        // Sorbarendezés pontszám szerint, holtverseny esetén névsor
        group.sort((a, b) => {
          const scoreA = a.totalScore !== undefined ? Number(a.totalScore) : -1;
          const scoreB = b.totalScore !== undefined ? Number(b.totalScore) : -1;
          if (scoreB !== scoreA) return scoreB - scoreA;
          return (a.fullName || "").localeCompare(b.fullName || "");
        });

        // Helyezések kiosztása
        group.forEach((p, index) => {
          // Kiszámoljuk a zónánkénti találatokat az archiváláshoz
          const shots = allShotsByCompetitor[p.id] || [];
          const hitZoneCounts = {};
          // Alapértelmezett zónák inicializálása
          (competition?.scoringValues || ['11', '10', '8', '5', '0', 'X']).forEach(z => hitZoneCounts[z] = 0);

          shots.forEach(s => {
            if (s.value) {
              hitZoneCounts[s.value] = (hitZoneCounts[s.value] || 0) + 1;
            }
          });

          finalParticipantsWithRank.push({
            ...p,
            rank: index + 1,
            hitZoneCounts: hitZoneCounts,
            finalShots: shots // Az összes lövést is elmentjük részletesen
          });
        });
      });

      // 3. Verseny adatok másolása az events_closed gyűjteménybe
      const closedEventRef = doc(db, "events_closed", id);
      const { id: compId, ...competitionData } = competition; // Kivesszük az ID-t a mezők közül
      batch.set(closedEventRef, {
        ...competitionData,
        status: 'Lezárt',
        closedAt: new Date().toISOString(),
        finalResultCount: finalParticipantsWithRank.length
      });

      // 4. Nevezők másolása a pontszámokkal, helyezésekkel és zónákkal
      for (const p of finalParticipantsWithRank) {
        const pRef = doc(db, "events_closed", id, "participants", p.id);
        batch.set(pRef, p);

        // Eredeti nevező törlése a folyamat végén
        const oldPRef = doc(db, "events", id, "participants", p.id);
        batch.delete(oldPRef);
      }

      // 5. Eredeti verseny dokumentum törlése
      batch.delete(eventRef);

      // 6. Végrehajtás
      await batch.commit();

      setShowFinalCloseConfirmModal(false);
      alert("A verseny sikeresen lezárva és archiválva!");
      navigate('/egyesulet-dashboard');

    } catch (e) {
      console.error("Hiba az archiválás során:", e);
      alert("Hiba történt a lezárás közben.");
    }
  };

  // --- ÚJ: REGISZTRÁCIÓS LISTA ELŐKÉSZÍTÉSE ---
  const registrationData = useMemo(() => {
    // Szűrés kereső alapján
    const filtered = participants.filter(p =>
      p.fullName?.toLowerCase().includes(regSearchTerm.toLowerCase())
    );

    // Két csoportra osztás
    const pending = filtered.filter(p => !localRegisteredIds.has(p.id))
      .sort((a, b) => a.fullName?.localeCompare(b.fullName));

    const arrived = filtered.filter(p => localRegisteredIds.has(p.id))
      .sort((a, b) => a.fullName?.localeCompare(b.fullName));

    return { pending, arrived };
  }, [participants, localRegisteredIds, regSearchTerm]);

  const menuItems = [
    { id: 'overview', label: 'Áttekintés', icon: <FiInfo /> },
    { id: 'competitors', label: 'Nevezők', icon: <FiUsers /> },
    { id: 'teams', label: 'Csapatbeosztás', icon: <FiGrid /> },
    { id: 'qr-scanner', label: 'QR Beolvasás', icon: <FiCamera /> },
    { id: 'results', label: 'Eredmények', icon: <FiBarChart2 /> },
    { id: 'settings', label: 'Beállítások', icon: <FiSettings /> },
  ];

  const filteredParticipants = useMemo(() => {
    const filtered = participants.filter(p =>
      p.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.club?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!sortConfig.key) return filtered;

    const ageOrder = {
      'mini': 1, 'gyerek': 2, 'junior': 3, 'kadet': 4,
      'fiatal_felnott': 5, 'felnott': 6, 'veteran': 7
    };

    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';

      // Speciális kezelés: Korosztály (logikai sorrend)
      if (sortConfig.key === 'ageCategory') {
        aVal = ageOrder[a.ageCategory] || 99;
        bVal = ageOrder[b.ageCategory] || 99;
      }
      // Speciális kezelés: Íjkategória (rövidítés alapján)
      else if (sortConfig.key === 'bowCategory') {
        aVal = a.bowCategory?.split('_').pop() || '';
        bVal = b.bowCategory?.split('_').pop() || '';
      }
      // Speciális kezelés: Csapat (számként kezelve)
      else if (sortConfig.key === 'team') {
        aVal = parseInt(a.team) || 999;
        bVal = parseInt(b.team) || 999;
      }
      // Általános szöveges rendezés (Név, stb.)
      else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [participants, searchTerm, sortConfig]);

  if (loading) {
    return (
      <div style={loadingContainerStyle}>
        <div style={spinnerStyle}></div>
        <p>Versenyiroda betöltése...</p>
      </div>
    );
  }

  const generateParticipantId = (p) => {
    if (!competition?.competitionDate || !p.userId) return "N/A";
    const dateParts = competition.competitionDate.split('-');
    if (dateParts.length !== 3) return "N/A";
    const yy = dateParts[0].slice(-2);
    const mm = dateParts[1];
    const dd = dateParts[2];
    const uidSuffix = p.userId.slice(-4);
    return `${yy}${mm}${dd}${uidSuffix}`;
  };

  const generateParticipantQrString = (p) => {
    const scoring = competition?.scoringValues?.join(',') || '11,10,8,5,0';
    const dnsId = generateParticipantId(p);
    const bowCat = p.bowCategory?.split('_').pop()?.toUpperCase() || p.bowCategory;
    const ageCat = translationMap[p.ageCategory] || p.ageCategory;
    const genderNum = p.gender === 'Férfi' ? 0 : 1;
    const targets = competition?.numberOfTargets || 0;

    // --- CRB (SZÁMSZERÍJ) SPECIÁLIS 1-LÖVÉS LOGIKA ---
    let shots = competition?.shotsPerTarget || 0;
    if (competition?.crossbowSingleShot && (bowCat === 'CRB' || bowCat === 'SZAMSZERIJ')) {
      shots = 1;
    }

    const totalShots = (Number(targets) || 0) * (Number(shots) || 0);

    return `${scoring};${totalShots};${dnsId};${p.fullName};${bowCat};${ageCat};${genderNum};${targets};${shots}`;
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredParticipants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredParticipants.map(p => p.id)));
    }
  };

  const toggleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  return (
    <div style={pageContainerStyle} className="office-page-container">
      {/* --- MEGJEGYZÉS POPUP --- */}
      {activeComment && (
        <div
          onClick={() => setActiveComment(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999, backgroundColor: 'transparent'
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: Math.min(activeComment.x, window.innerWidth - 300),
              top: Math.min(activeComment.y, window.innerHeight - 200),
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              width: '280px',
              overflow: 'hidden',
              border: '1px solid #ddd'
            }}
          >
            <div style={{ backgroundColor: '#b30000', color: 'white', padding: '8px 15px', fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Megjegyzés</span>
              <FiX style={{ cursor: 'pointer' }} onClick={() => setActiveComment(null)} />
            </div>
            <div style={{ padding: '15px', fontSize: '0.9rem', color: '#333', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
              {activeComment.text}
            </div>
          </div>
        </div>
      )}
      {/* --- ARCHERY NEXUS STÍLUSÚ SIKERES MENTÉS MODAL --- */}
      {scannedSaveSuccessData && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '450px' }}>
            {/* Zöld pipa visszajelző ikon */}
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#e6fffa',
              color: '#2c7a7b',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              margin: '0 auto 15px auto',
              fontWeight: 'bold'
            }}>
              ✓
            </div>

            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
              Sikeres rögzítés!
            </h3>
            
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.4' }}>
              <strong>{scannedSaveSuccessData.length} fő</strong> eredménye sikeresen rögzítésre került az Eredmények fülön!
            </p>

            {/* Rögzített íjászok és pontjaik áttekintő listája */}
            <div style={{
              backgroundColor: '#fafafa',
              border: '1px solid #eee',
              borderRadius: '8px',
              padding: '10px 15px',
              marginBottom: '20px',
              maxHeight: '150px',
              overflowY: 'auto',
              textAlign: 'left'
            }}>
              {scannedSaveSuccessData.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justify: 'space-between',
                  padding: '5px 0',
                  fontSize: '0.85rem',
                  borderBottom: idx < scannedSaveSuccessData.length - 1 ? '1px solid #eee' : 'none'
                }}>
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{item.fullName}</span>
                  <span style={{ color: '#b30000', fontWeight: 'bold' }}>{item.totalScore} pont</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                style={{
                  ...modalDeleteButtonStyle,
                  backgroundColor: '#28a745',
                  width: '100%'
                }}
                onClick={() => setScannedSaveSuccessData(null)}
              >
                Rendben, jöhet a következő!
              </button>
            </div>
          </div>
        </div>
      )}
      {/* --- EGYEDI TÖRLÉSI ABLAK (MODAL) --- */}
      {showDeleteModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <FiAlertTriangle size={50} color="#b30000" style={{ marginBottom: '15px' }} />
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Biztosan törlöd?</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Valóban törölni szeretnéd <br/>
              <strong>{participantToDelete?.fullName}</strong> <br/>
              nevezését? Ez a művelet végleges.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                style={modalCancelButtonStyle}
                onClick={() => {
                  setShowDeleteModal(false);
                  setParticipantToDelete(null);
                }}
              >
                Mégse
              </button>
              <button
                style={modalDeleteButtonStyle}
                onClick={handleFinalDeleteParticipant}
              >
                Igen, törlöm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- VÉGLEGES EREDMÉNY LEZÁRÁS MEGERŐSÍTŐ MODAL --- */}
      {showFinalCloseConfirmModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '450px' }}>
            <FiAlertTriangle size={50} color="#b30000" style={{ marginBottom: '15px' }} />
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Eredmények véglegesítése</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Biztosan lezárod a versenyt? <br/>
              <strong>Ezután az eredmények már nem módosíthatók manuálisan sem!</strong> <br/>
              A lezárásról minden résztvevő Push értesítést kap.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                style={modalCancelButtonStyle}
                onClick={() => setShowFinalCloseConfirmModal(false)}
              >
                Mégse
              </button>
              <button
                style={{ ...modalDeleteButtonStyle, backgroundColor: '#28a745' }}
                onClick={handleFinalConfirmCloseCompetition}
              >
                Igen, lezárom
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- LÁNCBONTÁS MEGERŐSÍTŐ ABLAK --- */}
      {showLinkBreakModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '450px' }}>
            <FiLink size={50} color="#b30000" style={{ marginBottom: '15px' }} />
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Láncolt nevek mozgatása</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Megtartod a láncot vagy szét szeretnéd bontani?
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexDirection: 'column' }}>
              <button
                style={{ ...qrButtonStyle, backgroundColor: '#28a745', border: 'none', padding: '12px' }}
                onClick={handleConfirmKeepLink}
              >
                Lánc megtartása
              </button>
              <button
                style={{ ...modalCancelButtonStyle, padding: '12px' }}
                onClick={handleConfirmBreakLink}
              >
                Lánc bontása
              </button>
              <button
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.85rem' }}
                onClick={() => { setShowLinkBreakModal(false); setPendingLinkData(null); }}
              >
                Mégse, maradjanak a csapatban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MANUÁLIS EREDMÉNY RÖGZÍTÉSE MODAL --- */}
      {showManualScoreModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '500px', textAlign: 'left' }}>
            <h3 style={{ ...resultGroupTitleStyle, marginBottom: '20px' }}>Eredmény manuális rögzítése</h3>

            {/* Név keresése felajánlásokkal */}
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Versenyző neve</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={formInputStyle}
                  type="text"
                  placeholder="Írd be a nevet..."
                  value={manualSearchTerm}
                  onChange={(e) => {
                    setManualSearchTerm(e.target.value);
                    if (manualScoreEntry.participant) {
                      setManualScoreEntry({ ...manualScoreEntry, participant: null });
                    }
                  }}
                />

                {manualSearchTerm.length >= 2 && !manualScoreEntry.participant && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    backgroundColor: 'white', border: '1px solid #ddd',
                    borderRadius: '8px', boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    zIndex: 10, maxHeight: '200px', overflowY: 'auto'
                  }}>
                    {participants
                      .filter(p => p.totalScore === undefined) // KISZŰRJÜK AZOKAT, AKIKNEK MÁR VAN EREDMÉNYE
                      .filter(p => p.fullName.toLowerCase().includes(manualSearchTerm.toLowerCase()))
                      .map(p => (
                        <div
                          key={p.id}
                          onClick={() => {
                            setManualScoreEntry({ ...manualScoreEntry, participant: p });
                            setManualSearchTerm(p.fullName);
                          }}
                          style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <strong>{p.fullName}</strong>
                          <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '10px' }}>
                            ({p.club || 'Egyéb'})
                          </span>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Kiválasztott íjász adatai */}
            {manualScoreEntry.participant && (
              <div style={{ ...profileRegCardStyle, backgroundColor: '#fff9f9', borderColor: '#ffcccc', marginTop: '10px', position: 'relative', minHeight: '110px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', paddingRight: '100px' }}>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>Egyesület:</strong> {manualScoreEntry.participant.club || 'Egyéb'}
                  </div>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>Kategória:</strong> {translationMap[manualScoreEntry.participant.bowCategory] || manualScoreEntry.participant.bowCategory}
                  </div>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>Korosztály:</strong> {translationMap[manualScoreEntry.participant.ageCategory] || manualScoreEntry.participant.ageCategory}
                  </div>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>Nem:</strong> {manualScoreEntry.participant.gender}
                  </div>
                </div>

                {/* ÉLŐ PONTSZÁM KIJELZÉS JOBB ALSÓ SAROKBAN */}
                <div style={{
                  position: 'absolute', right: '15px', bottom: '10px',
                  textAlign: 'right'
                }}>
                  <div style={{ fontSize: '0.7rem', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' }}>Pontszám</div>
                  <div style={{ fontSize: '2rem', fontWeight: '900', color: '#b30000', lineHeight: '1' }}>
                    {Object.entries(manualScoreEntry.zones).reduce((acc, [zone, count]) => {
                      const val = zone === 'X' ? 11 : parseInt(zone) || 0;
                      return acc + (val * (parseInt(count) || 0));
                    }, 0)}
                  </div>
                </div>
              </div>
            )}

            {/* Zónák bevitele */}
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Találatok száma (Zónánként)</label>

                {/* LÖVÉSSZÁMLÁLÓ (Pl. 0 / 40) */}
                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#666' }}>
                  Összes lövés:
                  <span style={{
                    marginLeft: '8px',
                    color: (() => {
                      const currentShots = Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0);
                      const bowCatShort = manualScoreEntry.participant?.bowCategory?.split('_').pop()?.toUpperCase() || '';
                      let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
                      if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
                        expectedShotsPerTarget = 1;
                      }
                      const maxShots = (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;
                      return currentShots === maxShots ? '#28a745' : (currentShots > maxShots ? '#dc3545' : '#b30000');
                    })()
                  }}>
                    {Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0)}
                    / {(() => {
                      const bowCatShort = manualScoreEntry.participant?.bowCategory?.split('_').pop()?.toUpperCase() || '';
                      let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
                      if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
                        expectedShotsPerTarget = 1;
                      }
                      return (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;
                    })()}
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                {(competition?.scoringValues || ['11', '10', '8', '5', '0'])
                  .sort((a, b) => {
                    const valA = a === 'X' ? 11.5 : parseInt(a);
                    const valB = b === 'X' ? 11.5 : parseInt(b);
                    return valB - valA;
                  })
                  .map(zone => (
                  <div key={zone} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{
                      textAlign: 'center', backgroundColor: '#b30000', color: 'white',
                      borderRadius: '4px', padding: '2px', fontSize: '0.8rem', fontWeight: 'bold'
                    }}>
                      {zone}p
                    </div>
                    <input
                      type="number"
                      min="0"
                      style={{ ...formInputStyle, textAlign: 'center', padding: '5px' }}
                      value={manualScoreEntry.zones[zone] || ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : parseInt(e.target.value);
                        setManualScoreEntry({
                          ...manualScoreEntry,
                          zones: { ...manualScoreEntry.zones, [zone]: val }
                        });
                        if (manualScoreError) setManualScoreError(null);
                      }}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>

            {manualScoreError && (
              <div style={{
                marginTop: '20px',
                padding: '12px 15px',
                backgroundColor: '#fff5f5',
                border: '1px solid #feb2b2',
                borderRadius: '8px',
                color: '#c53030',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: 'bold'
              }}>
                <FiAlertTriangle size={20} />
                {manualScoreError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
              <button style={modalCancelButtonStyle} onClick={() => setShowManualScoreModal(false)}>Mégse</button>
              <button
                style={{
                  ...qrButtonStyle,
                  padding: '12px 20px',
                  backgroundColor: (() => {
                    if (competition?.status === 'Lezárt') return '#ccc';
                    const currentShots = Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0);
                    const bowCatShort = manualScoreEntry.participant?.bowCategory?.split('_').pop()?.toUpperCase() || '';
                    let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
                    if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
                      expectedShotsPerTarget = 1;
                    }
                    const maxShots = (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;
                    return currentShots === maxShots ? '#b30000' : '#ccc';
                  })(),
                  borderColor: (() => {
                    if (competition?.status === 'Lezárt') return '#ccc';
                    const currentShots = Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0);
                    const bowCatShort = manualScoreEntry.participant?.bowCategory?.split('_').pop()?.toUpperCase() || '';
                    let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
                    if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
                      expectedShotsPerTarget = 1;
                    }
                    const maxShots = (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;
                    return currentShots === maxShots ? '#b30000' : '#ccc';
                  })(),
                  cursor: (() => {
                    if (competition?.status === 'Lezárt') return 'not-allowed';
                    const currentShots = Object.values(manualScoreEntry.zones).reduce((acc, curr) => acc + (parseInt(curr) || 0), 0);
                    const bowCatShort = manualScoreEntry.participant?.bowCategory?.split('_').pop()?.toUpperCase() || '';
                    let expectedShotsPerTarget = Number(competition?.shotsPerTarget) || 0;
                    if (competition?.crossbowSingleShot && (bowCatShort === 'CRB' || bowCatShort === 'SZAMSZERIJ')) {
                      expectedShotsPerTarget = 1;
                    }
                    const maxShots = (Number(competition?.numberOfTargets) || 0) * expectedShotsPerTarget;
                    return currentShots === maxShots ? 'pointer' : 'default';
                  })()
                }}
                onClick={handleSaveManualScore}
              >
                Eredmény hozzáadása
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- QR KÓD NYOMTATÓ MODAL (PORTAL) --- */}
      {showQrModal && createPortal(
        <div id="archery-nexus-qr-print-portal">
          <div className="no-print print-header-controls">
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ margin: 0 }}>Nevezők QR kódjai ({selectedIds.size} fő kijelölve)</h3>
              <p style={{ margin: '5px 0 0 0', color: '#666' }}>{competition?.competitionName}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={printButtonStyle} onClick={() => window.print()}>
                <FiPrinter /> Nyomtatás
              </button>
              <button style={closeButtonStyle} onClick={() => setShowQrModal(false)}>
                <FiX /> Bezárás
              </button>
            </div>
          </div>

          <div className="printable-qr-grid" style={qrGridStyle}>
            {participants
              .filter(p => selectedIds.has(p.id))
              .map((p) => (
                <div key={p.id} style={qrCardStyle} className="qr-card">
                  <QRCodeSVG value={generateParticipantQrString(p)} size={80} />
                  <div style={qrCardInfoStyle}>
                    <div style={qrCardNameStyle}>
                      {p.fullName} ({p.gender === 'Férfi' ? 'Férfi' : 'Nő'})
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '2px', fontWeight: 'bold' }}>
                      {p.club || p.team || '-'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#333', marginTop: '4px' }}>
                      {translationMap[p.bowCategory] || p.bowCategory} - {translationMap[p.ageCategory] || p.ageCategory}
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <style>{`
            #archery-nexus-qr-print-portal {
              position: fixed; top: 0; left: 0; right: 0; bottom: 0;
              background: white; z-index: 10005; padding: 40px; overflow-y: auto;
            }
            .print-header-controls {
              display: flex; justify-content: space-between; align-items: center;
              margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #eee;
              max-width: 1000px; margin: 0 auto 30px;
            }

            @media print {
              @page { 
                size: A4 portrait; 
                margin: 5mm !important; 
              }

              * { box-sizing: border-box !important; }

              html, body {
                width: 100% !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
                background: white !important;
              }

              /* A teljes fő React gyökeret elrejtjük */
              #root { display: none !important; }
              body > *:not(#archery-nexus-qr-print-portal) { display: none !important; }

              #archery-nexus-qr-print-portal {
                position: static !important;
                display: block !important;
                width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
              }

              .no-print { display: none !important; }

              .printable-qr-grid {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                grid-auto-rows: 32mm !important;
                gap: 1.5mm !important;
                width: 100% !important;
              }

              .qr-card {
                border: 0.5px solid #ccc !important;
                padding: 2mm 3mm !important;
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                overflow: hidden !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                background: white !important;
                height: 32mm !important;
              }

              * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* --- ÚJ NEVEZŐ HOZZÁADÁSA MODAL --- */}
      {showAddModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '600px', textAlign: 'left' }}>
            <h3 style={{ ...resultGroupTitleStyle, marginBottom: '20px' }}>Helyszíni nevező regisztrálása</h3>

            <div style={formGridStyle}>
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Név</label>
                <input
                  style={formInputStyle}
                  type="text"
                  value={newParticipant.fullName}
                  onChange={(e) => setNewParticipant({...newParticipant, fullName: e.target.value})}
                  placeholder="Versenyző teljes neve"
                />
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Egyesület / Csapat</label>
                <input
                  style={formInputStyle}
                  type="text"
                  value={newParticipant.club}
                  onChange={(e) => setNewParticipant({...newParticipant, club: e.target.value})}
                  placeholder="Egyesület neve (üresen: Egyéb)"
                />
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Íjkategória</label>
                <select
                  style={{ ...selectStyle, width: '100%' }}
                  value={newParticipant.bowCategory}
                  onChange={(e) => setNewParticipant({...newParticipant, bowCategory: e.target.value})}
                >
                  <option value="">Válassz kategóriát...</option>
                  {competition?.categories?.map(catId => {
                    const label = translationMap[catId] || catId;
                    return <option key={catId} value={catId}>{label}</option>;
                  })}
                </select>
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Korosztály</label>
                <select
                  style={{ ...selectStyle, width: '100%' }}
                  value={newParticipant.ageCategory}
                  onChange={(e) => setNewParticipant({...newParticipant, ageCategory: e.target.value})}
                >
                  <option value="">Válassz korosztályt...</option>
                  {competition?.ageCategories?.map(ageId => {
                    const label = translationMap[ageId] || ageId;
                    return <option key={ageId} value={ageId}>{label}</option>;
                  })}
                </select>
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Nem</label>
                <select
                  style={{ ...selectStyle, width: '100%' }}
                  value={newParticipant.gender}
                  onChange={(e) => setNewParticipant({...newParticipant, gender: e.target.value})}
                >
                  <option value="Férfi">Férfi</option>
                  <option value="Nő">Nő</option>
                </select>
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Ebéd adag</label>
                <input
                  style={formInputStyle}
                  type="number"
                  min="0"
                  value={newParticipant.lunchCount}
                  onChange={(e) => setNewParticipant({...newParticipant, lunchCount: parseInt(e.target.value) || 0})}
                />
              </div>

              <div style={{ ...inputGroupStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Megjegyzés</label>
                <textarea
                  style={{ ...formInputStyle, minHeight: '60px', paddingTop: '10px' }}
                  value={newParticipant.comment}
                  onChange={(e) => setNewParticipant({...newParticipant, comment: e.target.value})}
                  placeholder="Pl. csapattárs kérése..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '25px' }}>
              <button style={modalCancelButtonStyle} onClick={() => setShowAddModal(false)}>Mégse</button>
              <button style={qrButtonStyle} onClick={handleSaveNewParticipant}>Mentés és Rögzítés</button>
            </div>
          </div>
        </div>
      )}

      {/* --- CSAPATBEOSZTÁS NYOMTATÓ MODAL (PORTAL) --- */}
      {showTeamsPrintModal && createPortal(
        <div id="archery-nexus-print-portal">
          <div className="no-print print-header-controls">
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ margin: 0 }}>Csapatbeosztás (Rajtlista)</h3>
              <p style={{ margin: '5px 0 0 0', color: '#666' }}>{competition?.competitionName}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ ...printButtonStyle, backgroundColor: '#b30000' }} onClick={() => window.print()}><FiPrinter /> Nyomtatás</button>
              <button style={{ ...closeButtonStyle, backgroundColor: '#6c757d' }} onClick={() => setShowTeamsPrintModal(false)}><FiX /> Bezárás</button>
            </div>
          </div>

          <div className="print-grid-container">
            {/* 1. LAP FEJLÉC - CSAK EGYSZER */}
            <div className="print-brand-header">
              <img src={logo} alt="Logo" style={{ height: '70px' }} />
              <div style={{ textAlign: 'left' }}>
                <h1 style={{ margin: 0, color: '#b30000', fontSize: '3rem', fontWeight: '900' }}>ArcheryNexus</h1>
              </div>
            </div>

            {/* STRUKTÚRA TÁBLÁZAT A MARGÓKHOZ */}
            <table className="print-main-table-wrapper">
              <thead>
                <tr>
                  <td>
                    <div className="print-page-top-margin"></div>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="print-teams-grid">
                      {teamsData.map((team) => (
                        <div key={team.number} className="print-team-card">
                          <h4 style={teamPrintHeaderStyle}>{team.number}. Csapat</h4>
                          <table style={teamPrintTableStyle}>
                            <thead>
                              <tr>
                                <th style={{ ...teamPrintThStyle, width: '40%' }}>Név</th>
                                <th style={{ ...teamPrintThStyle, width: '25%' }}>Kategória</th>
                                <th style={{ ...teamPrintThStyle, width: '35%' }}>Korosztály</th>
                              </tr>
                            </thead>
                            <tbody>
                              {team.members.map((m) => (
                                <tr key={m.id}>
                                  <td style={teamPrintTdStyle}><strong>{m.fullName}</strong></td>
                                  <td style={teamPrintTdStyle}>{translationMap[m.bowCategory]?.split('(')[0]?.trim() || m.bowCategory}</td>
                                  <td style={teamPrintTdStyle}>{translationMap[m.ageCategory] || m.ageCategory}</td>
                                </tr>
                              ))}
                              {team.members.length === 0 && (
                                <tr><td colSpan="3" style={{ ...teamPrintTdStyle, color: '#ccc', fontStyle: 'italic', textAlign: 'center' }}>Üres csapat</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <div className="print-footer-nexus">
                      <span>Nyomtatva: {new Date().toLocaleDateString('hu-HU')}</span>
                      <span>www.archerynexus.com</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <style>{`
            #archery-nexus-print-portal {
              position: fixed; top: 0; left: 0; right: 0; bottom: 0;
              background: white; z-index: 10005; padding: 40px; overflow-y: auto;
            }
            .print-header-controls {
              display: flex; justify-content: space-between; align-items: center;
              margin-bottom: 30px; padding-bottom: 20px; border-bottom: 1px solid #eee;
              max-width: 1100px; margin: 0 auto 30px;
            }
            .print-grid-container { max-width: 1100px; margin: 0 auto; }
            .print-main-table-wrapper { width: 100%; border-collapse: collapse; border: none; }
            .print-page-top-margin { height: 0px; }
            .print-brand-header, .print-footer-nexus { display: none; }

            .print-teams-grid {
              display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;
            }
            .print-team-card { border: 1px solid #eee; border-radius: 8px; padding: 15px; }

            @media print {
              @page { size: A4 portrait; margin: 0 !important; }

              /* ATOMBIZTOS MÉRETEZÉS */
              * { box-sizing: border-box !important; }
              html, body {
                width: 210mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
                background: white !important;
              }
              #root { display: none !important; }
              body > *:not(#archery-nexus-print-portal) { display: none !important; }

              #archery-nexus-print-portal {
                position: static !important;
                display: block !important;
                width: 210mm !important;
                margin: 0 auto !important;
                padding: 10mm 15mm !important;
                overflow: visible !important;
              }

              #archery-nexus-print-portal * { visibility: visible !important; }

              .no-print { display: none !important; }

              .print-brand-header {
                display: flex !important;
                align-items: center !important;
                gap: 25px !important;
                margin-bottom: 15px !important;
                border-bottom: 4px solid #b30000 !important;
                padding-bottom: 10px !important;
                width: 100% !important;
              }

              .print-page-top-margin {
                display: block !important;
                height: 15mm !important; /* EZ TOLJA LE A TARTALMAT MINDEN LAPON */
              }

              .print-teams-grid {
                display: grid !important;
                grid-template-columns: 1fr 1fr 1fr !important;
                gap: 5mm !important;
                width: 100% !important;
                justify-content: center !important;
              }

              .print-team-card {
                border: 0.5px solid #000 !important;
                padding: 3mm !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                background: white !important;
              }

              .print-footer-nexus {
                display: flex !important;
                justify-content: space-between !important;
                font-size: 10px !important;
                color: #555 !important;
                border-top: 0.5px solid #eee !important;
                padding-top: 5px !important;
                margin-top: 10mm !important;
              }

              * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* OLDALSÁV (SIDEBAR) */}
      <div style={sidebarStyle} className="no-print">
        <div style={sidebarHeaderStyle}>
          <button onClick={() => navigate('/egyesulet-dashboard')} style={backButtonStyle}>
            <FiArrowLeft /> Vissza
          </button>
          <h3 style={sidebarTitleStyle}>{competition?.competitionName}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span style={statusBadgeStyle}>{competition?.status || 'Aktív'}</span>
            {isArchived && (
              <span style={{
                backgroundColor: '#eee',
                color: '#666',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                textAlign: 'center',
                border: '1px solid #ddd'
              }}>
                ARCHIVÁLT
              </span>
            )}
          </div>
        </div>

        <nav style={navStyle}>
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              style={tabButtonStyle(activeTab === item.id)}
            >
              <span style={iconStyle}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* FŐ TARTALOM (CONTENT AREA) */}
      <div style={contentStyle} className="no-print">
        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h2>{menuItems.find(m => m.id === activeTab)?.label}</h2>
            {activeTab === 'competitors' && !isArchived && (
              <button
                onClick={handleToggleRegistrationLock}
                style={{
                  backgroundColor: isRegistrationClosed ? '#6c757d' : '#b30000',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
              >
                {isRegistrationClosed ? (
                  <><FiX size={16} /> Regisztráció újranyitása</>
                ) : (
                  <><FiCheck size={16} /> Regisztráció lezárása</>
                )}
              </button>
            )}
          </div>
          <div style={headerStatsStyle}>
            <span>Nevezők: <strong>{participants.length}</strong></span>
            <span>Regisztrált: <strong>{arrivedCount}</strong></span>
            {activeTab === 'teams' && (
              <button
                style={{
                  backgroundColor: '#b30000',
                  color: 'white', border: 'none', borderRadius: '8px', padding: '8px 15px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem'
                }}
                onClick={() => setShowTeamsPrintModal(true)}
              >
                <FiPrinter /> Csapatlista nyomtatása
              </button>
            )}
          </div>
        </header>

        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
            {/* VERSENY ALAPADATOK */}
            <div style={{ ...mainCardStyle, minHeight: 'auto' }}>
              <h4 style={{ color: '#b30000', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                Verseny részletei
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '1rem' }}>{isRegistrationClosed ? '🔒' : '🔓'}</span>
                  <span style={statusBadgeStyle}>{competition?.status || 'Aktív'}</span>
                </div>
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <p style={{ margin: '8px 0' }}><strong>Dátum:</strong> {competition?.competitionDate}</p>
                  <p style={{ margin: '8px 0' }}><strong>Helyszín:</strong> {competition?.location}</p>
                  <p style={{ margin: '4px 0 8px 0', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                    <strong>GPS:</strong> {competition?.gpsLatitude && competition?.gpsLongitude ? `${competition.gpsLatitude}, ${competition.gpsLongitude}` : 'Nincs megadva'}
                  </p>
                </div>
                <div style={{ borderLeft: '1px solid #eee', paddingLeft: '20px' }}>
                  <p style={{ margin: '8px 0' }}><strong>Típus:</strong> {competition?.competitionType?.toUpperCase()}</p>
                  <p style={{ margin: '8px 0' }}><strong>Célok száma:</strong> {competition?.numberOfTargets}</p>
                  <p style={{ margin: '8px 0' }}><strong>Lövés/cél:</strong> {competition?.shotsPerTarget}</p>
                </div>
              </div>
            </div>

            {/* STATISZTIKAI WIDGETEK */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
              <div style={statWidgetStyle}>
                <div style={statLabelStyle}>Részvétel</div>
                <div style={statValueStyle}>{arrivedCount} / {participants.length}</div>
                <div style={statSubLabelStyle}>{arrivedCount} fő megérkezett ({Math.round((arrivedCount / (participants.length || 1)) * 100)}%)</div>
              </div>

              <div style={statWidgetStyle}>
                <div style={statLabelStyle}>Ebéd összesítő</div>
                <div style={{ ...statValueStyle, color: '#f58231' }}>{dashboardStats.totalLunches} adag</div>
                <div style={statSubLabelStyle}>{dashboardStats.arrivedLunches} adag "megérkezett"</div>
              </div>

              <div
                style={{ ...statWidgetStyle, cursor: 'pointer', userSelect: 'none' }}
                onMouseDown={() => setShowFinancials(true)}
                onMouseUp={() => setShowFinancials(false)}
                onMouseLeave={() => setShowFinancials(false)}
                title="Tartsd nyomva a megtekintéshez"
              >
                <div style={statLabelStyle}>Pénzügyi becslés</div>
                <div style={{
                  ...statValueStyle,
                  color: '#28a745',
                  filter: showFinancials ? 'none' : 'blur(8px)',
                  transition: 'filter 0.2s ease'
                }}>
                  {dashboardStats.totalEntryFees + dashboardStats.totalLunchFees} Ft
                </div>
                <div style={{
                  ...statSubLabelStyle,
                  filter: showFinancials ? 'none' : 'blur(4px)',
                  transition: 'filter 0.2s ease'
                }}>
                  {dashboardStats.totalEntryFees} Ft nevezés + {dashboardStats.totalLunchFees} Ft ebéd
                </div>
              </div>
            </div>

            {/* KATEGÓRIA BONTÁS */}
            <div style={mainCardStyle}>
              <h4 style={{ color: '#333', marginBottom: '15px', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Nevezők kategóriánként</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                {Object.values(dashboardStats.categories).sort((a,b) => b.count - a.count).map(cat => (
                  <div key={cat.label} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>{cat.label}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{cat.count} fő</span>
                      <span style={{ fontSize: '0.75rem', color: '#28a745' }}>{cat.arrived} megérkezett</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'competitors' && (
          <div style={{ ...mainCardStyle, padding: '25px 15px' }}>
            <div style={actionBarStyle}>
              <div style={searchContainerStyle}>
                <FiSearch style={searchIconStyle} />
                <input
                  type="text"
                  placeholder="Keresés név vagy egyesület alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={searchInputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={qrButtonStyle} onClick={() => setShowQrModal(true)}>
                  <MdQrCode size={18} /> QR kódok generálása
                </button>
                <button style={exportButtonStyle} onClick={() => alert('Exporting...')}>
                  <FiDownload /> Export (.csv)
                </button>
              </div>
            </div>

            {/* --- ÚJ: CSAPAT REGISZTRÁCIÓS ÁLLAPOTJELZŐ RÁCS --- */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '10px',
              border: '1px solid #eee',
              position: 'relative'
            }}>
              {teamsData.map((team) => {
                const arrivedInTeam = team.members.filter(m => localRegisteredIds.has(m.id)).length;
                const totalInTeam = team.members.length;

                let bgColor = '#fff'; // Fehér (Senki nincs ott)
                let borderColor = '#ddd';

                if (totalInTeam > 0) {
                  if (arrivedInTeam === totalInTeam) {
                    bgColor = '#28a745'; // Zöld (Mindenki ott van)
                    borderColor = '#218838';
                  } else if (arrivedInTeam > 0) {
                    bgColor = '#ffc107'; // Sárga (Vannak már ott)
                    borderColor = '#e0a800';
                  }
                }

                return (
                  <div
                    key={team.number}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPos({ x: rect.left, y: rect.top + window.scrollY });
                      setHoveredTeam(team);
                    }}
                    onMouseLeave={() => setHoveredTeam(null)}
                    style={{
                      width: '28px',
                      height: '28px',
                      backgroundColor: bgColor,
                      border: `1px solid ${borderColor}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: arrivedInTeam > 0 ? '#fff' : '#888',
                      cursor: 'help',
                      transition: 'all 0.2s ease',
                      boxShadow: arrivedInTeam > 0 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {team.number}
                  </div>
                );
              })}

              {/* TOOLTIP A CSAPAT RÉSZLETEKKEL */}
              {hoveredTeam && (
                <div style={{
                  position: 'fixed',
                  left: `${tooltipPos.x}px`,
                  top: `${tooltipPos.y - (hoveredTeam.members.length * 25 + 50)}px`,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  padding: '12px',
                  zIndex: 1000,
                  minWidth: '200px',
                  pointerEvents: 'none'
                }}>
                  <div style={{ borderBottom: '1px solid #eee', marginBottom: '8px', paddingBottom: '5px', fontWeight: 'bold', color: '#b30000' }}>
                    {hoveredTeam.number}. csapat részletei
                  </div>
                  {hoveredTeam.members.length > 0 ? (
                    hoveredTeam.members.map(m => {
                      const arrived = localRegisteredIds.has(m.id);
                      return (
                        <div key={m.id} style={{
                          fontSize: '0.85rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '3px 0',
                          color: arrived ? '#333' : '#bbb',
                          fontWeight: arrived ? 'bold' : 'normal'
                        }}>
                          <span>{arrived ? '✅' : '⏳'} {m.fullName}</span>
                          <span style={{ fontSize: '0.7rem', backgroundColor: '#eee', padding: '1px 4px', borderRadius: '3px', color: '#666', marginLeft: '10px' }}>
                            {m.bowCategory?.split('_').pop()?.toUpperCase()}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: '#bbb', fontStyle: 'italic' }}>Üres csapat</div>
                  )}
                </div>
              )}
            </div>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ ...tableStyle, minWidth: '1200px' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ cursor: 'pointer' }}
                        onChange={toggleSelectAll}
                        checked={selectedIds.size === filteredParticipants.length && filteredParticipants.length > 0}
                      />
                    </th>
                    <th style={{ ...thStyle, width: '35px' }}>ID.</th>

                    {/* RENDEZHETŐ OSZLOPOK */}
                    <th
                      style={{ ...thStyle, width: '160px', cursor: 'pointer' }}
                      onClick={() => requestSort('fullName')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        Név {sortConfig.key === 'fullName' ? (sortConfig.direction === 'asc' ? <FiArrowUp color="#b30000" /> : <FiArrowDown color="#b30000" />) : <FiArrowUp color="#ccc" />}
                      </div>
                    </th>

                    <th
                      style={{ ...thStyle, cursor: 'pointer' }}
                      onClick={() => requestSort('team')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        Csapat {sortConfig.key === 'team' ? (sortConfig.direction === 'asc' ? <FiArrowUp color="#b30000" /> : <FiArrowDown color="#b30000" />) : <FiArrowUp color="#ccc" />}
                      </div>
                    </th>

                    <th style={thStyle}>Engedély</th>

                    <th
                      style={{ ...thStyle, cursor: 'pointer' }}
                      onClick={() => requestSort('bowCategory')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        Kategória {sortConfig.key === 'bowCategory' ? (sortConfig.direction === 'asc' ? <FiArrowUp color="#b30000" /> : <FiArrowDown color="#b30000" />) : <FiArrowUp color="#ccc" />}
                      </div>
                    </th>

                    <th
                      style={{ ...thStyle, cursor: 'pointer' }}
                      onClick={() => requestSort('ageCategory')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        Korosztály {sortConfig.key === 'ageCategory' ? (sortConfig.direction === 'asc' ? <FiArrowUp color="#b30000" /> : <FiArrowDown color="#b30000" />) : <FiArrowUp color="#ccc" />}
                      </div>
                    </th>

                    <th style={thStyle}>Nem</th>
                    <th style={thStyle}>Nevezési díj</th>
                    <th style={thStyle}>Ebéd</th>
                    <th style={thStyle}>Ebéd díja</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Infó</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Megérkezett</th>
                    <th style={thStyle}>Törlés</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipants.length > 0 ? (
                    filteredParticipants.map((p) => {
                      const lunchPrice = Number(competition?.lunchPrice) || 0;
                      const lunchFee = (Number(p.lunchCount) || 0) * lunchPrice;
                    const displayId = generateParticipantId(p);

                    return (
                      <tr key={p.id} style={trStyle}>
                        <td style={{ ...tdStyle, width: '40px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => toggleSelectOne(p.id)}
                          />
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            width: '35px',
                            padding: '12px 5px',
                            position: 'relative',
                            overflow: 'visible'
                          }}
                        >
                          <div
                            style={{
                              fontFamily: 'monospace',
                              fontWeight: 'bold',
                              color: '#b30000',
                              width: revealedIds.has(p.id) ? 'auto' : '35px',
                              overflow: revealedIds.has(p.id) ? 'visible' : 'hidden',
                              whiteSpace: 'nowrap',
                              maskImage: revealedIds.has(p.id) ? 'none' : 'linear-gradient(to right, black 20%, transparent 90%)',
                              WebkitMaskImage: revealedIds.has(p.id) ? 'none' : 'linear-gradient(to right, black 20%, transparent 90%)',
                              cursor: 'pointer',
                              userSelect: 'none',
                              position: revealedIds.has(p.id) ? 'absolute' : 'relative',
                              zIndex: revealedIds.has(p.id) ? 100 : 1,
                              backgroundColor: revealedIds.has(p.id) ? 'white' : 'transparent',
                              padding: revealedIds.has(p.id) ? '2px 8px' : '0',
                              borderRadius: '4px',
                              boxShadow: revealedIds.has(p.id) ? '0 4px 10px rgba(0,0,0,0.2)' : 'none',
                              left: '5px',
                              top: revealedIds.has(p.id) ? '8px' : 'auto',
                              border: revealedIds.has(p.id) ? '1px solid #ddd' : 'none'
                            }}
                            onMouseDown={() => setRevealedIds(new Set([p.id]))}
                            onMouseUp={() => setRevealedIds(new Set())}
                            onMouseLeave={() => setRevealedIds(new Set())}
                            title="Hold for full ID"
                          >
                            {displayId}
                          </div>
                        </td>
                          <td
                            style={{
                              ...tdStyle,
                              width: '160px',
                              padding: '12px 8px',
                              position: 'relative',
                              overflow: 'visible'
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 'bold',
                                width: revealedNames.has(p.id) ? 'auto' : '160px',
                                overflow: revealedNames.has(p.id) ? 'visible' : 'hidden',
                                whiteSpace: 'nowrap',
                                maskImage: revealedNames.has(p.id) ? 'none' : 'linear-gradient(to right, black 70%, transparent 98%)',
                                WebkitMaskImage: revealedNames.has(p.id) ? 'none' : 'linear-gradient(to right, black 70%, transparent 98%)',
                                cursor: 'pointer',
                                userSelect: 'none',
                                position: revealedNames.has(p.id) ? 'absolute' : 'relative',
                                zIndex: revealedNames.has(p.id) ? 100 : 1,
                                backgroundColor: revealedNames.has(p.id) ? 'white' : 'transparent',
                                padding: revealedNames.has(p.id) ? '2px 8px' : '0',
                                borderRadius: '4px',
                                boxShadow: revealedNames.has(p.id) ? '0 4px 10px rgba(0,0,0,0.2)' : 'none',
                                left: '5px',
                                top: revealedNames.has(p.id) ? '8px' : 'auto',
                                border: revealedNames.has(p.id) ? '1px solid #ddd' : 'none'
                              }}
                              onMouseDown={() => setRevealedNames(new Set([p.id]))}
                              onMouseUp={() => setRevealedNames(new Set())}
                              onMouseLeave={() => setRevealedNames(new Set())}
                              title="Hold for full name"
                            >
                              {p.fullName}
                            </div>
                          </td>
                          <td style={tdStyle}>{p.team || '-'}</td>
                          <td style={tdStyle}>{p.licenseNumber || p.license || '-'}</td>
                          <td style={tdStyle}>{translationMap[p.bowCategory] || p.bowCategory}</td>
                          <td style={tdStyle}>{translationMap[p.ageCategory] || p.ageCategory}</td>
                          <td style={tdStyle}>{p.gender === 'Férfi' ? 'Male' : 'Female'}</td>
                          <td style={tdStyle}>
                            {competition?.ageFees?.[p.ageCategory] ? `${competition.ageFees[p.ageCategory]} Ft` : (p.entryFee ? `${p.entryFee} Ft` : '-')}
                          </td>
                          <td style={tdStyle}>{p.lunchCount || 0} portions</td>
                          <td style={tdStyle}>{lunchFee > 0 ? `${lunchFee} Ft` : '-'}</td>

                          {/* MEGJEGYZÉS IKON */}
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {p.comment ? (
                              <button
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setActiveComment({
                                    text: p.comment,
                                    x: rect.left,
                                    y: rect.top + window.scrollY
                                  });
                                }}
                                style={{ background: 'none', border: 'none', color: '#b30000', cursor: 'pointer', padding: '5px' }}
                                title="View comment"
                              >
                                <FiMessageSquare size={18} />
                              </button>
                            ) : '-'}
                          </td>

                          {/* MEGÉRKEZETT GOMB */}
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <button
                              onClick={() => handleToggleRegistration(p.id)}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isRegistrationClosed ? 'not-allowed' : 'pointer',
                                transition: '0.2s',
                                opacity: (isRegistrationClosed && !localRegisteredIds.has(p.id)) ? 0.6 : 1,
                                backgroundColor: localRegisteredIds.has(p.id)
                                  ? '#28a745'
                                  : (isRegistrationClosed ? '#dc3545' : '#eee'),
                                color: (localRegisteredIds.has(p.id) || isRegistrationClosed) ? '#fff' : '#888'
                              }}
                            >
                              {localRegisteredIds.has(p.id) ? (
                                <FiCheck size={18} strokeWidth={3} />
                              ) : (
                                isRegistrationClosed ? <FiX size={18} strokeWidth={3} /> : <FiAlertCircle size={18} />
                              )}
                            </button>
                          </td>

                          <td style={tdStyle}>
                            <button
                              onClick={() => handleDeleteParticipant(p)}
                              style={{
                                ...deleteButtonStyle,
                                opacity: isRegistrationClosed ? 0.3 : 1,
                                pointerEvents: isRegistrationClosed ? 'none' : 'auto',
                                cursor: isRegistrationClosed ? 'not-allowed' : 'pointer'
                              }}
                              title={isRegistrationClosed ? "Regisztráció lezárva" : "Delete registration"}
                            >
                              <FiTrash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                    <td colSpan="13" style={emptyTdStyle}>Nem található nevező.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
            <button
              style={{
                ...qrButtonStyle,
                backgroundColor: isRegistrationClosed ? '#ccc' : '#b30000',
                borderColor: isRegistrationClosed ? '#ccc' : '#b30000',
                cursor: isRegistrationClosed ? 'not-allowed' : 'pointer',
                opacity: isRegistrationClosed ? 0.7 : 1
              }}
              onClick={() => {
                if (!isRegistrationClosed) setShowAddModal(true);
              }}
            >
              + Új nevező
            </button>
          </div>
        </div>
      )}

        {activeTab === 'teams' && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={isArchived ? null : handleDragStart}
            onDragEnd={isArchived ? null : handleDragEnd}
          >
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

              {/* BAL OLDAL: CSAPATRÁCS */}
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
                {teamsData.map((team) => (
                  <div key={team.number} id={`team-${team.number}`}>
                    <TeamBox
                      teamNumber={team.number}
                      participants={team.members}
                      linkBaseId={linkBaseId}
                      onLinkClick={handleToggleLink}
                      isArchived={isArchived}
                    />
                  </div>
                ))}
              </div>

              {/* JOBB OLDAL: VÁRÓLISTA (POOL) */}
              <div style={{ width: '300px', position: 'sticky', top: '20px' }}>
                <div style={{ ...mainCardStyle, minHeight: '600px', padding: '15px', display: 'flex', flexDirection: 'column' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#b30000', borderBottom: '2px solid #b30000', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                    Várólista
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>{poolData.length} fő</span>
                  </h4>

                  {/* Várólista kereső */}
                  <div style={{ position: 'relative', marginBottom: '15px' }}>
                    <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
                    <input
                      type="text"
                      placeholder="Név keresése..."
                      value={poolSearchTerm}
                      onChange={(e) => setPoolSearchTerm(e.target.value)}
                      style={{ ...formInputStyle, paddingLeft: '35px', fontSize: '0.85rem' }}
                    />
                  </div>

                  {/* Droppable terület a visszahúzáshoz */}
                  <PoolBox
                    participants={poolData}
                    onLinkClick={handleToggleLink}
                    isArchived={isArchived}
                  />
                </div>
              </div>
            </div>

            <DragOverlay dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: { active: { opacity: '0.5', }, },
              }),
            }}>
              {activeId ? (() => {
                const activeParticipant = participants.find(p => p.id === activeId);
                const movingMembers = activeParticipant?.linkId
                  ? participants.filter(p => p.linkId === activeParticipant.linkId)
                  : [activeParticipant];

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {movingMembers.map(m => (
                      <SortableParticipant
                        key={m.id}
                        id={m.id}
                        participant={m}
                        isOverlay
                      />
                    ))}
                  </div>
                );
              })() : null}
            </DragOverlay>
          </DndContext>
        )}

        {activeTab === 'results' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            {/* HIBAÜZENET A LEZÁRÁSHOZ */}
            {showCloseCompetitionError && (
              <div style={{
                padding: '15px 20px',
                backgroundColor: '#fff5f5',
                border: '1px solid #feb2b2',
                borderRadius: '12px',
                color: '#c53030',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                animation: 'shake 0.5s ease'
              }}>
                <FiAlertCircle size={24} />
                <span>Hiba: Még nem minden versenyzőnek van rögzített pontszáma!</span>
                <style>{`
                  @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                  }
                `}</style>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <h3 style={{ ...resultGroupTitleStyle, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>Eredmények</h3>
                {!isArchived && (
                  <button
                    onClick={handleCloseCompetition}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: '0.3s',
                      backgroundColor: (participants.length > 0 && participants.every(p => p.totalScore !== undefined)) ? '#b30000' : '#ccc',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiCheck size={18} /> Eredmények lezárása
                  </button>
                )}
              </div>
            </div>

            <div style={actionBarStyle}>
              <div style={searchContainerStyle}>
                <FiSearch style={searchIconStyle} />
                <input
                  type="text"
                  placeholder="Keresés a rangsorban (név vagy egyesület)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={searchInputStyle}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  style={{
                    ...qrButtonStyle,
                    backgroundColor: competition?.status === 'Lezárt' ? '#ccc' : '#b30000',
                    borderColor: competition?.status === 'Lezárt' ? '#ccc' : '#b30000',
                    cursor: competition?.status === 'Lezárt' ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    if (competition?.status === 'Lezárt') {
                      alert("A verseny le van zárva, az eredmények már nem módosíthatók.");
                      return;
                    }
                    setManualScoreEntry({ participant: null, zones: {} });
                    setManualSearchTerm('');
                    setShowManualScoreModal(true);
                  }}
                >
                  <FiPlus /> Eredmény manuális rögzítése
                </button>

                {/* ÉREM SZÁMLÁLÓ MEGJELENÍTÉSE */}
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', backgroundColor: '#f8f9fa', padding: '10px 20px', borderRadius: '12px', border: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '1.2rem' }}>🥇</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#d4af37' }}>{medalStats.gold}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '1.2rem' }}>🥈</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#aaa9ad' }}>{medalStats.silver}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '1.2rem' }}>🥉</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#cd7f32' }}>{medalStats.bronze}</span>
                  </div>
                  <div style={{ marginLeft: '10px', fontSize: '0.8rem', color: '#888', fontWeight: 'bold', textTransform: 'uppercase' }}>Szükséges érmek</div>
                </div>
              </div>
            </div>

            {resultsData.length > 0 ? (
              <div style={resultsGridStyle}>
                {resultsData.map((group, idx) => (
                  <div key={idx} style={{ ...mainCardStyle, margin: 0, padding: '15px' }}>
                    <h3 style={{ ...resultGroupTitleStyle, fontSize: '1rem', marginBottom: '10px' }}>
                      {translationMap[group.bowCategory] || group.bowCategory} - {translationMap[group.ageCategory] || group.ageCategory} - {group.gender}
                    </h3>
                    <table style={{ ...tableStyle, fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: '30px', padding: '8px 5px' }}>#</th>
                          <th style={{ ...thStyle, padding: '8px 5px' }}>Név</th>
                          <th style={{ ...thStyle, textAlign: 'right', padding: '8px 5px' }}>Pont</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.members.map((p, pIdx) => {
                          const hasScore = p.totalScore !== undefined;
                          const rank = pIdx + 1;
                          let podiumIcon = null;

                          // Csak akkor kap ikont, ha van pontja
                          if (hasScore) {
                            if (rank === 1) podiumIcon = "🥇";
                            else if (rank === 2) podiumIcon = "🥈";
                            else if (rank === 3) podiumIcon = "🥉";
                          }

                          return (
                            <tr key={p.id} style={{
                              ...trStyle,
                              backgroundColor: (hasScore && rank <= 3) ? '#fffef2' : 'transparent',
                              opacity: hasScore ? 1 : 0.5 // Elhalványítás, ha nincs pont
                            }}>
                              <td style={{ ...tdStyle, fontWeight: 'bold', padding: '8px 5px', color: hasScore ? '#333' : '#ccc' }}>
                                {podiumIcon || (hasScore ? `${rank}.` : '-')}
                              </td>
                              <td style={{ ...tdStyle, padding: '8px 5px', color: hasScore ? '#333' : '#ccc' }}>
                                <div style={{ fontWeight: 'bold' }}>{p.fullName}</div>
                                <div style={{ fontSize: '0.7rem', color: hasScore ? '#888' : '#ddd' }}>{p.club || '-'}</div>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', padding: '8px 5px', color: hasScore ? '#b30000' : '#ccc' }}>
                                {hasScore ? p.totalScore : '0'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : (
              <div style={mainCardStyle}>
                <div style={placeholderStyle}>
                  <p>Nem található eredmény a megadott keresési feltételekkel.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- QR-BEOLVASÓ FÜL TARTALMA (ÁTADVA A MENTŐ FÜGGVÉNY IS) --- */}
{activeTab === 'qr-scanner' && (
  <QrCodeScannerTab 
    onScanSuccess={handleQrScanResult} 
    onSaveScores={handleSaveScannedScores}
  />
)}

{/* --- BEÁLLÍTÁSOK FÜL TARTALMA --- */}
{activeTab === 'settings' && (
  <div style={mainCardStyle}>
    <div style={placeholderStyle}>
      <p>Ez a modul (Beállítások) fejlesztés alatt áll.</p>
      <p style={{ color: '#888', fontSize: '0.9rem' }}>Hamarosan érkezik a funkciók bekötése.</p>
    </div>
  </div>
)}
      </div>
    </div>
  );
};

// --- STÍLUSOK ---

const pageContainerStyle = {
  display: 'flex',
  height: 'calc(100vh - 64px)',
  backgroundColor: '#f4f6f8',
  fontFamily: 'Arial, sans-serif'
};

const sidebarStyle = {
  width: '220px',
  minWidth: '220px',
  backgroundColor: '#ffffff',
  borderRight: '1px solid #e0e4e8',
  display: 'flex',
  flexDirection: 'column',
  padding: '15px'
};

const sidebarHeaderStyle = {
  marginBottom: '30px',
  borderBottom: '1px solid #eee',
  paddingBottom: '20px'
};

const backButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'none',
  border: 'none',
  color: '#666',
  cursor: 'pointer',
  fontSize: '14px',
  marginBottom: '15px',
  padding: 0
};

const sidebarTitleStyle = {
  margin: '0 0 10px 0',
  fontSize: '1.2rem',
  color: '#333',
  fontWeight: 'bold'
};

const statusBadgeStyle = {
  backgroundColor: '#e6fffa',
  color: '#2c7a7b',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 'bold'
};

const navStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '5px'
};

const tabButtonStyle = (isActive) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 15px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: isActive ? '#fbeaea' : 'transparent',
  color: isActive ? '#b30000' : '#555',
  fontWeight: isActive ? 'bold' : 'normal',
  textAlign: 'left',
  cursor: 'pointer',
  transition: '0.2s',
  fontSize: '15px'
});

const iconStyle = {
  fontSize: '18px',
  display: 'flex'
};

const contentStyle = {
  flex: 1,
  padding: '20px 15px',
  overflowY: 'auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '25px'
};

const headerStatsStyle = {
  display: 'flex',
  gap: '20px',
  color: '#666'
};

const mainCardStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '25px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  minHeight: '400px'
};

const actionBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  gap: '20px'
};

const searchContainerStyle = {
  position: 'relative',
  flex: 1,
  maxWidth: '400px'
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#888'
};

const formInputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const searchInputStyle = {
  ...formInputStyle,
  paddingLeft: '40px'
};

const exportButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 15px',
  backgroundColor: '#fafafa',
  border: '1px solid #ddd',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#333'
};

const qrButtonStyle = {
  ...exportButtonStyle,
  backgroundColor: '#b30000',
  color: 'white',
  border: '1px solid #b30000'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '10px'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px 8px',
  borderBottom: '2px solid #eee',
  color: '#666',
  fontSize: '14px',
  fontWeight: 'bold'
};

const tdStyle = {
  padding: '12px 8px',
  borderBottom: '1px solid #eee',
  fontSize: '14px'
};

const trStyle = {
  transition: '0.1s',
  ':hover': {
    backgroundColor: '#f9fafb'
  }
};

const emptyTdStyle = {
  padding: '40px',
  textAlign: 'center',
  color: '#888',
  fontStyle: 'italic'
};

const commentIconStyle = {
  cursor: 'help',
  fontSize: '1.2rem'
};

const deleteButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#b30000',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px',
  borderRadius: '4px',
  transition: '0.2s'
};

const participantStatusStyle = (status) => ({
  backgroundColor: status === 'Megérkezett' ? '#e6fffa' : '#f7fafc',
  color: status === 'Megérkezett' ? '#2c7a7b' : '#4a5568',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 'bold'
});

const infoGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '20px'
};

const infoCardStyle = {
  padding: '20px',
  borderRadius: '10px',
  border: '1px solid #eee',
  backgroundColor: '#fafafa'
};

const placeholderStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '300px',
  textAlign: 'center'
};

const loadingContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh'
};

const spinnerStyle = {
  width: '40px',
  height: '40px',
  border: '4px solid #f3f3f3',
  borderTop: '#b30000 4px solid',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '15px'
};

// --- MODAL STÍLUSOK ---

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2000,
  backdropFilter: 'blur(3px)'
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '30px',
  borderRadius: '15px',
  textAlign: 'center',
  maxWidth: '400px',
  width: '95%',
  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
  boxSizing: 'border-box',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const modalDeleteButtonStyle = {
  backgroundColor: '#b30000',
  color: 'white',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const modalCancelButtonStyle = {
  backgroundColor: '#eee',
  color: '#333',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

// --- CSAPATBEOSZTÁS STÍLUSOK ---

const teamsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '20px',
  padding: '10px'
};

const teamBoxStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '200px',
  overflow: 'hidden'
};

const teamHeaderStyle = {
  backgroundColor: '#fff',
  padding: '10px 15px',
  borderBottom: '1px solid #eee',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 'bold',
  fontSize: '0.9rem',
  color: '#333'
};

const teamCountStyle = {
  fontSize: '0.8rem',
  color: '#888',
  fontWeight: 'normal'
};

const teamBodyStyle = {
  padding: '10px',
  flex: 1,
  minHeight: '100px'
};

const emptyTeamTextStyle = {
  color: '#ccc',
  fontSize: '0.8rem',
  textAlign: 'center',
  marginTop: '20px',
  fontStyle: 'italic'
};

const catBadgeStyle = {
  backgroundColor: '#e9ecef',
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  color: '#495057'
};

const teamCardCommentStyle = {
  fontSize: '0.7rem',
  color: '#b30000',
  fontStyle: 'italic',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  borderTop: '1px solid #f0f0f0',
  paddingTop: '4px',
  marginTop: '2px'
};

const resultGroupTitleStyle = {
  margin: '0 0 20px 0',
  paddingBottom: '10px',
  borderBottom: '2px solid #b30000',
  color: '#b30000',
  fontSize: '1.2rem',
  fontWeight: 'bold'
};

const resultsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '20px',
  padding: '10px'
};

// --- QR MODAL STÍLUSOK ---

const qrModalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#fff',
  zIndex: 3000,
  overflowY: 'auto',
  padding: '40px'
};

const qrModalContentStyle = {
  maxWidth: '1000px',
  margin: '0 auto',
  backgroundColor: '#fff'
};

const qrModalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '40px',
  paddingBottom: '20px',
  borderBottom: '1px solid #eee'
};

const printButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  backgroundColor: '#b30000',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 'bold'
};

const closeButtonStyle = {
  ...printButtonStyle,
  backgroundColor: '#6c757d'
};

const qrGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '15px'
};

const qrCardStyle = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '10px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '15px',
  backgroundColor: '#fff',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '100px'
};

const qrCardInfoStyle = {
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  overflow: 'hidden'
};

const qrCardNameStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  lineHeight: '1.2',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

// --- CSAPAT NYOMTATÁS STÍLUSOK ---

const teamPrintCardStyle = {
  border: '1px solid #eee',
  borderRadius: '8px',
  padding: '15px',
  backgroundColor: '#fff'
};

const teamPrintHeaderStyle = {
  margin: '0 0 10px 0',
  paddingBottom: '5px',
  borderBottom: '2px solid #333',
  fontSize: '1.1rem'
};

const teamPrintTableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const teamPrintThStyle = {
  textAlign: 'left',
  fontSize: '0.8rem',
  color: '#666',
  paddingBottom: '5px'
};

const teamPrintTdStyle = {
  padding: '5px 8px 5px 0',
  fontSize: '0.9rem',
  borderBottom: '1px solid #f0f0f0',
  textAlign: 'left'
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '15px'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '5px'
};

const labelStyle = {
  fontSize: '0.85rem',
  fontWeight: 'bold',
  color: '#555'
};

const selectStyle = {
  ...formInputStyle
};

const profileRegCardStyle = {
  backgroundColor: '#f8f9fa',
  padding: '15px',
  borderRadius: '10px',
  border: '1px solid #eee',
  marginBottom: '15px'
};

const regCardStyle = (isRegistered, isClosed = false) => {
  const isMissing = !isRegistered && isClosed;
  return {
    padding: '12px 15px',
    borderRadius: '10px',
    backgroundColor: isRegistered ? '#28a745' : (isMissing ? '#dc3545' : '#fff'),
    color: (isRegistered || isMissing) ? '#fff' : '#333',
    border: isRegistered ? '1px solid #218838' : (isMissing ? '1px solid #c82333' : '1px solid #eee'),
    cursor: isClosed ? 'default' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: (isRegistered || isMissing) ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };
};

const statWidgetStyle = {
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  border: '1px solid #eee'
};

const statLabelStyle = {
  fontSize: '0.85rem',
  color: '#888',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  marginBottom: '5px'
};

const statValueStyle = {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  color: '#333'
};

const statSubLabelStyle = {
  fontSize: '0.8rem',
  color: '#666',
  marginTop: '5px'
};

export default CompetitionOffice;





